import { HIDDEN_LANGUAGE, LANGUAGE, type Language } from 'src/data/generation';
import { GENERATION_TOOL, type GenerationTool } from 'src/data/generationTool';
import type { ID } from 'src/lib/collection';

export const TONE_OF_VOICE_PERSONALITY = {
  advisor: 'advisor',
  motivator: 'motivator',
  entertainer: 'entertainer',
  empathetic: 'empathetic',
  trendsetter: 'trendsetter',
  innovator: 'innovator',
  educator: 'educator',
} as const;
export type ToneOfVoicePersonality =
  (typeof TONE_OF_VOICE_PERSONALITY)[keyof typeof TONE_OF_VOICE_PERSONALITY];

export const PRESET_TONE_OF_VOICE = {
  engaging: 'engaging',
  ...TONE_OF_VOICE_PERSONALITY,
} as const;
export type PresetToneOfVoice = (typeof PRESET_TONE_OF_VOICE)[keyof typeof PRESET_TONE_OF_VOICE];

export const MANUAL_TONE_OF_VOICE = {
  friendly: 'friendly',
  confident: 'confident',
  witty: 'witty',
  urgent: 'urgent',
  creative: 'creative',
  informative: 'informative',
} as const;
export type ManualToneOfVoice = (typeof MANUAL_TONE_OF_VOICE)[keyof typeof MANUAL_TONE_OF_VOICE];

export const MANUAL_TONE_OF_VOICE_PROPS: Record<ManualToneOfVoice, { title: string }> = {
  [MANUAL_TONE_OF_VOICE.friendly]: {
    title: 'Friendly',
  },
  [MANUAL_TONE_OF_VOICE.confident]: {
    title: 'Confident',
  },
  [MANUAL_TONE_OF_VOICE.witty]: {
    title: 'Witty',
  },
  [MANUAL_TONE_OF_VOICE.urgent]: {
    title: 'Urgent',
  },
  [MANUAL_TONE_OF_VOICE.creative]: {
    title: 'Creative',
  },

  [MANUAL_TONE_OF_VOICE.informative]: {
    title: 'Informative',
  },
};

interface PresetTovProps {
  id: PresetToneOfVoice;
  title: string;
  description: string;
  label?: string;
}

export const PRESET_TONE_OF_VOICE_PROPS: Record<PresetToneOfVoice, PresetTovProps> = {
  [PRESET_TONE_OF_VOICE.engaging]: {
    id: PRESET_TONE_OF_VOICE.engaging,
    title: 'Neutral',
    description: 'The standard AI tone of voice',
    label: '',
  },
  [PRESET_TONE_OF_VOICE.innovator]: {
    id: PRESET_TONE_OF_VOICE.innovator,
    title: 'Innovator',
    description: 'Share new developments and ideas',
    label: 'Apple, Tesla, Google',
  },
  [PRESET_TONE_OF_VOICE.trendsetter]: {
    id: PRESET_TONE_OF_VOICE.trendsetter,
    title: 'Trendsetter',
    description: 'Connect with social media natives',
    label: 'Netflix, TikTok, Spotify',
  },
  [PRESET_TONE_OF_VOICE.advisor]: {
    id: PRESET_TONE_OF_VOICE.advisor,
    title: 'Advisor',
    description: 'Establish credibility with expert advice',
    label: 'J.P. Morgan, PwC, McKinsey & Co',
  },
  [PRESET_TONE_OF_VOICE.motivator]: {
    id: PRESET_TONE_OF_VOICE.motivator,
    title: 'Motivator',
    description: 'Inspire readers, driving them toward action',
    label: 'Nike, Under Armour, Peloton',
  },
  [PRESET_TONE_OF_VOICE.entertainer]: {
    id: PRESET_TONE_OF_VOICE.entertainer,
    title: 'Entertainer',
    description: 'Create fun copy that makes people smile',
    label: 'Snickers, Old Spice, Duolingo',
  },
  [PRESET_TONE_OF_VOICE.empathetic]: {
    id: PRESET_TONE_OF_VOICE.empathetic,
    title: 'Empathetic',
    description: 'Show care and connect on a deeper level',
    label: 'Dove, Airbnb, Cadbury',
  },
  [PRESET_TONE_OF_VOICE.educator]: {
    id: PRESET_TONE_OF_VOICE.educator,
    title: 'Educator',
    description: 'Provide clear, helpful content to educate the audience',
    label: 'Coursera, edX, Khan Academy',
  },
};

export const PRESET_TONE_OF_VOICE_NON_ENGLISH_PROPS: Record<
  string,
  Record<string, PresetTovProps>
> = {
  [LANGUAGE.de]: {
    [PRESET_TONE_OF_VOICE.advisor]: {
      id: PRESET_TONE_OF_VOICE.advisor,
      title: 'Berater',
      description: 'Glaubwürdigkeit durch fachkundige Beratung aufbauen.',
    },
    [PRESET_TONE_OF_VOICE.motivator]: {
      id: PRESET_TONE_OF_VOICE.motivator,
      title: 'Motivator',
      description: 'Leser inspirieren und sie zu Handlungen treiben.',
    },
    [PRESET_TONE_OF_VOICE.entertainer]: {
      id: PRESET_TONE_OF_VOICE.entertainer,
      title: 'Entertainer',
      description:
        'Unterhaltsame Texte erstellen, die den Menschen ein Lächeln ins Gesicht zaubern.',
    },
    [PRESET_TONE_OF_VOICE.empathetic]: {
      id: PRESET_TONE_OF_VOICE.empathetic,
      title: 'Empathisch',
      description: 'Fürsorge zeigen und auf einer tieferen Ebene verbinden.',
    },
    [PRESET_TONE_OF_VOICE.trendsetter]: {
      id: PRESET_TONE_OF_VOICE.trendsetter,
      title: 'Trendsetter',
      description:
        'Mit Social-Media-Nutzern in einem Ton kommunizieren, der sich anfühlt wie das Texten mit einem Freund.',
    },
    [PRESET_TONE_OF_VOICE.innovator]: {
      id: PRESET_TONE_OF_VOICE.innovator,
      title: 'Innovator',
      description:
        'Neue Entwicklungen und Ideen auf eine selbstbewusste, anspruchsvolle Weise teilen.',
    },
    [PRESET_TONE_OF_VOICE.educator]: {
      id: PRESET_TONE_OF_VOICE.educator,
      title: 'Pädagoge',
      description: 'Klare und hilfreiche Inhalte bereitstellen, um das Publikum zu bilden.',
    },
  },
  [LANGUAGE.de_in]: {
    [PRESET_TONE_OF_VOICE.advisor]: {
      id: PRESET_TONE_OF_VOICE.advisor,
      title: 'Berater',
      description: 'Glaubwürdigkeit durch Expertenrat aufbauen.',
    },
    [PRESET_TONE_OF_VOICE.motivator]: {
      id: PRESET_TONE_OF_VOICE.motivator,
      title: 'Motivator',
      description: 'Die Leser inspirieren und sie zum Handeln bewegen.',
    },
    [PRESET_TONE_OF_VOICE.entertainer]: {
      id: PRESET_TONE_OF_VOICE.entertainer,
      title: 'Entertainer',
      description: 'Lustige Texte erstellen, die den Leuten ein Lächeln ins Gesicht zaubern.',
    },
    [PRESET_TONE_OF_VOICE.empathetic]: {
      id: PRESET_TONE_OF_VOICE.empathetic,
      title: 'Empathisch',
      description: 'Sorge zeigen und auf einer tieferen Ebene verbinden.',
    },
    [PRESET_TONE_OF_VOICE.trendsetter]: {
      id: PRESET_TONE_OF_VOICE.trendsetter,
      title: 'Trendsetter',
      description:
        'Mit Social-Media-Nutzern in einem Ton kommunizieren, der sich anfühlt, als würde man mit einem Freund schreiben.',
    },
    [PRESET_TONE_OF_VOICE.innovator]: {
      id: PRESET_TONE_OF_VOICE.innovator,
      title: 'Innovator',
      description: 'Neue Entwicklungen und Ideen selbstbewusst und elegant teilen.',
    },
    [PRESET_TONE_OF_VOICE.educator]: {
      id: PRESET_TONE_OF_VOICE.educator,
      title: 'Erzieher',
      description: 'Klare, hilfreiche Inhalte bereitstellen, um das Publikum zu informieren.',
    },
  },
  [LANGUAGE.de_ch]: {
    [PRESET_TONE_OF_VOICE.advisor]: {
      id: PRESET_TONE_OF_VOICE.advisor,
      title: 'Berater',
      description: 'Glaubwürdigkeit mit fachkundigem Rat aufbauen.',
    },
    [PRESET_TONE_OF_VOICE.motivator]: {
      id: PRESET_TONE_OF_VOICE.motivator,
      title: 'Motivator',
      description: 'Leser inspirieren und sie zu Handlungen motivieren.',
    },
    [PRESET_TONE_OF_VOICE.entertainer]: {
      id: PRESET_TONE_OF_VOICE.entertainer,
      title: 'Unterhalter',
      description: 'Unterhaltsame Texte erstellen, die Menschen zum Lächeln bringen.',
    },
    [PRESET_TONE_OF_VOICE.empathetic]: {
      id: PRESET_TONE_OF_VOICE.empathetic,
      title: 'Empathisch',
      description: 'Fürsorge zeigen und auf einer tieferen Ebene verbinden.',
    },
    [PRESET_TONE_OF_VOICE.trendsetter]: {
      id: PRESET_TONE_OF_VOICE.trendsetter,
      title: 'Trendsetter',
      description:
        'Mit Social-Media-Natives in einem Ton kommunizieren, der sich wie eine Nachricht an einen Freund anfühlt.',
    },
    [PRESET_TONE_OF_VOICE.innovator]: {
      id: PRESET_TONE_OF_VOICE.innovator,
      title: 'Innovator',
      description: 'Neue Entwicklungen und Ideen selbstbewusst und anspruchsvoll präsentieren.',
    },
    [PRESET_TONE_OF_VOICE.educator]: {
      id: PRESET_TONE_OF_VOICE.educator,
      title: 'Lehrer',
      description: 'Klare, hilfreiche Inhalte bieten, um das Publikum zu informieren.',
    },
  },
  [LANGUAGE.el]: {
    [PRESET_TONE_OF_VOICE.advisor]: {
      id: PRESET_TONE_OF_VOICE.advisor,
      title: 'Σύμβουλος',
      description: 'Εδραιώστε την αξιοπιστία σας με εξειδικευμένες συμβουλές.',
    },
    [PRESET_TONE_OF_VOICE.motivator]: {
      id: PRESET_TONE_OF_VOICE.motivator,
      title: 'Εμπνευστής',
      description: 'Εμπνεύστε τους αναγνώστες και παρακινήστε τους να δράσουν.',
    },
    [PRESET_TONE_OF_VOICE.entertainer]: {
      id: PRESET_TONE_OF_VOICE.entertainer,
      title: 'Διασκεδαστής',
      description: 'Δημιουργήστε διασκεδαστικό περιεχόμενο που κάνει τους ανθρώπους να χαμογελούν.',
    },
    [PRESET_TONE_OF_VOICE.empathetic]: {
      id: PRESET_TONE_OF_VOICE.empathetic,
      title: 'Εμφατικός',
      description: 'Δείξτε φροντίδα και συνδεθείτε σε ένα βαθύτερο επίπεδο.',
    },
    [PRESET_TONE_OF_VOICE.trendsetter]: {
      id: PRESET_TONE_OF_VOICE.trendsetter,
      title: 'Πρωτοπόρος',
      description:
        'Συνδεθείτε με τους χρήστες των κοινωνικών δικτύων με έναν τόνο σαν να στέλνετε μήνυμα σε φίλο.',
    },
    [PRESET_TONE_OF_VOICE.innovator]: {
      id: PRESET_TONE_OF_VOICE.innovator,
      title: 'Καινοτόμος',
      description: 'Μοιραστείτε νέες εξελίξεις και ιδέες με αυτοπεποίθηση και κομψότητα.',
    },
    [PRESET_TONE_OF_VOICE.educator]: {
      id: PRESET_TONE_OF_VOICE.educator,
      title: 'Εκπαιδευτής',
      description: 'Παρέχετε σαφές και χρήσιμο περιεχόμενο για να εκπαιδεύσετε το κοινό σας.',
    },
  },
  [LANGUAGE.es]: {
    [PRESET_TONE_OF_VOICE.advisor]: {
      id: PRESET_TONE_OF_VOICE.advisor,
      title: 'Asesor',
      description: 'Establecer credibilidad con asesoramiento experto.',
    },
    [PRESET_TONE_OF_VOICE.motivator]: {
      id: PRESET_TONE_OF_VOICE.motivator,
      title: 'Motivador',
      description: 'Inspira a los lectores, impulsándolos a actuar.',
    },
    [PRESET_TONE_OF_VOICE.entertainer]: {
      id: PRESET_TONE_OF_VOICE.entertainer,
      title: 'Animador',
      description: 'Crea textos divertidos que hagan sonreír a las personas.',
    },
    [PRESET_TONE_OF_VOICE.empathetic]: {
      id: PRESET_TONE_OF_VOICE.empathetic,
      title: 'Empático',
      description: 'Muestra cuidado y conéctate a un nivel más profundo.',
    },
    [PRESET_TONE_OF_VOICE.trendsetter]: {
      id: PRESET_TONE_OF_VOICE.trendsetter,
      title: 'Pionero de tendencias',
      description:
        'Conectar con los nativos de las redes sociales en un tono que se sienta como enviar un mensaje a un amigo.',
    },
    [PRESET_TONE_OF_VOICE.innovator]: {
      id: PRESET_TONE_OF_VOICE.innovator,
      title: 'Innovador',
      description: 'Compartir nuevos desarrollos e ideas de manera segura y sofisticada.',
    },
    [PRESET_TONE_OF_VOICE.educator]: {
      id: PRESET_TONE_OF_VOICE.educator,
      title: 'Educador',
      description: 'Proveer contenido claro y útil para educar a la audiencia.',
    },
  },
  [LANGUAGE.es_la]: {
    [PRESET_TONE_OF_VOICE.advisor]: {
      id: PRESET_TONE_OF_VOICE.advisor,
      title: 'Asesor',
      description: 'Establezca credibilidad con consejos expertos.',
    },
    [PRESET_TONE_OF_VOICE.motivator]: {
      id: PRESET_TONE_OF_VOICE.motivator,
      title: 'Motivador',
      description: 'Inspire a los lectores y motívelos a tomar acción.',
    },
    [PRESET_TONE_OF_VOICE.entertainer]: {
      id: PRESET_TONE_OF_VOICE.entertainer,
      title: 'Animador',
      description: 'Cree contenido divertido que haga sonreír a las personas.',
    },
    [PRESET_TONE_OF_VOICE.empathetic]: {
      id: PRESET_TONE_OF_VOICE.empathetic,
      title: 'Empático',
      description: 'Demuestre empatía y conéctese en un nivel más profundo.',
    },
    [PRESET_TONE_OF_VOICE.trendsetter]: {
      id: PRESET_TONE_OF_VOICE.trendsetter,
      title: 'Vanguardista',
      description:
        'Conéctese con los usuarios de redes sociales con un tono cercano, como si enviara un mensaje a un amigo.',
    },
    [PRESET_TONE_OF_VOICE.innovator]: {
      id: PRESET_TONE_OF_VOICE.innovator,
      title: 'Innovador',
      description: 'Comparta nuevos desarrollos e ideas de manera segura y sofisticada.',
    },
    [PRESET_TONE_OF_VOICE.educator]: {
      id: PRESET_TONE_OF_VOICE.educator,
      title: 'Educador',
      description: 'Ofrezca contenido claro y útil para educar a su audiencia.',
    },
  },
  [LANGUAGE.es_cs]: {
    [PRESET_TONE_OF_VOICE.advisor]: {
      id: PRESET_TONE_OF_VOICE.advisor,
      title: 'Asesor',
      description: 'Establezca credibilidad con consejos de expertos.',
    },
    [PRESET_TONE_OF_VOICE.motivator]: {
      id: PRESET_TONE_OF_VOICE.motivator,
      title: 'Motivador',
      description: 'Inspire a los lectores y motívelos a actuar.',
    },
    [PRESET_TONE_OF_VOICE.entertainer]: {
      id: PRESET_TONE_OF_VOICE.entertainer,
      title: 'Animador',
      description: 'Cree contenido divertido que haga sonreír a la gente.',
    },
    [PRESET_TONE_OF_VOICE.empathetic]: {
      id: PRESET_TONE_OF_VOICE.empathetic,
      title: 'Empático',
      description: 'Demuestre empatía y conéctese a un nivel más profundo.',
    },
    [PRESET_TONE_OF_VOICE.trendsetter]: {
      id: PRESET_TONE_OF_VOICE.trendsetter,
      title: 'Innovador',
      description:
        'Conéctese con los nativos de redes sociales con un tono cercano, como enviar un mensaje a un amigo.',
    },
    [PRESET_TONE_OF_VOICE.innovator]: {
      id: PRESET_TONE_OF_VOICE.innovator,
      title: 'Innovador',
      description: 'Comparta nuevos desarrollos e ideas de manera segura y sofisticada.',
    },
    [PRESET_TONE_OF_VOICE.educator]: {
      id: PRESET_TONE_OF_VOICE.educator,
      title: 'Educador',
      description: 'Ofrezca contenido claro y útil para educar a la audiencia.',
    },
  },
  [LANGUAGE.tr]: {
    [PRESET_TONE_OF_VOICE.advisor]: {
      id: PRESET_TONE_OF_VOICE.advisor,
      title: 'Danışman',
      description: 'Uzman tavsiyesi ile güvenilirlik oluşturun.',
    },
    [PRESET_TONE_OF_VOICE.motivator]: {
      id: PRESET_TONE_OF_VOICE.motivator,
      title: 'Motive eden',
      description: 'Okuyucuları ilham vererek harekete geçirin.',
    },
    [PRESET_TONE_OF_VOICE.entertainer]: {
      id: PRESET_TONE_OF_VOICE.entertainer,
      title: 'Eğlendirici',
      description: 'İnsanları güldüren eğlenceli içerikler oluşturun.',
    },
    [PRESET_TONE_OF_VOICE.empathetic]: {
      id: PRESET_TONE_OF_VOICE.empathetic,
      title: 'Empatik',
      description: 'Şefkat gösterin ve derin bir bağ kurun.',
    },
    [PRESET_TONE_OF_VOICE.trendsetter]: {
      id: PRESET_TONE_OF_VOICE.trendsetter,
      title: 'Moda belirleyic',
      description:
        'Sosyal medya kullanıcılarıyla, bir arkadaşa mesaj atıyormuş gibi bir tonla bağlantı kurun.',
    },
    [PRESET_TONE_OF_VOICE.innovator]: {
      id: PRESET_TONE_OF_VOICE.innovator,
      title: 'Yenilikçi',
      description:
        'Yeni gelişmeleri ve fikirleri kendinden emin ve sofistike bir şekilde paylaşın.',
    },
    [PRESET_TONE_OF_VOICE.educator]: {
      id: PRESET_TONE_OF_VOICE.educator,
      title: 'Eğitimci',
      description: 'Hedef kitlenizi eğitmek için net ve faydalı içerik sağlayın.',
    },
  },
  [LANGUAGE.fr]: {
    [PRESET_TONE_OF_VOICE.advisor]: {
      id: PRESET_TONE_OF_VOICE.advisor,
      title: 'Conseiller',
      description: "Établir la crédibilité avec des conseils d'expert.",
    },
    [PRESET_TONE_OF_VOICE.motivator]: {
      id: PRESET_TONE_OF_VOICE.motivator,
      title: 'Motivant',
      description: 'Inspirer les lecteurs, les poussant à agir.',
    },
    [PRESET_TONE_OF_VOICE.entertainer]: {
      id: PRESET_TONE_OF_VOICE.entertainer,
      title: 'Amuseur',
      description: 'Créer du contenu amusant qui fait sourire les gens.',
    },
    [PRESET_TONE_OF_VOICE.empathetic]: {
      id: PRESET_TONE_OF_VOICE.empathetic,
      title: 'Empathique',
      description: 'Montrer de l’empathie et établir une connexion plus profonde.',
    },
    [PRESET_TONE_OF_VOICE.trendsetter]: {
      id: PRESET_TONE_OF_VOICE.trendsetter,
      title: 'Précurseur',
      description:
        'Se connecter avec les natifs des réseaux sociaux dans un ton qui ressemble à un texto à un ami.',
    },
    [PRESET_TONE_OF_VOICE.innovator]: {
      id: PRESET_TONE_OF_VOICE.innovator,
      title: 'Innovateur',
      description: 'Partager de nouvelles idées et développements avec confiance et élégance.',
    },
    [PRESET_TONE_OF_VOICE.educator]: {
      id: PRESET_TONE_OF_VOICE.educator,
      title: 'Éducateur',
      description: 'Fournir un contenu clair et utile pour éduquer le public.',
    },
  },
  [LANGUAGE.fr_qu]: {
    [PRESET_TONE_OF_VOICE.advisor]: {
      id: PRESET_TONE_OF_VOICE.advisor,
      title: 'Conseiller',
      description: "Établissez votre crédibilité grâce à des conseils d'experts.",
    },
    [PRESET_TONE_OF_VOICE.motivator]: {
      id: PRESET_TONE_OF_VOICE.motivator,
      title: 'Motivant',
      description: "Inspirez les lecteurs et poussez-les à passer à l'action.",
    },
    [PRESET_TONE_OF_VOICE.entertainer]: {
      id: PRESET_TONE_OF_VOICE.entertainer,
      title: 'Divertissant',
      description: 'Créez un contenu amusant qui fait sourire les gens.',
    },
    [PRESET_TONE_OF_VOICE.empathetic]: {
      id: PRESET_TONE_OF_VOICE.empathetic,
      title: 'Empathique',
      description: 'Montrez de la bienveillance et connectez-vous à un niveau plus profond.',
    },
    [PRESET_TONE_OF_VOICE.trendsetter]: {
      id: PRESET_TONE_OF_VOICE.trendsetter,
      title: 'Tendance',
      description:
        'Connectez-vous avec les adeptes des réseaux sociaux dans un ton qui rappelle un texto entre amis.',
    },
    [PRESET_TONE_OF_VOICE.innovator]: {
      id: PRESET_TONE_OF_VOICE.innovator,
      title: 'Innovateur',
      description: 'Partagez de nouvelles idées et avancées avec confiance et sophistication.',
    },
    [PRESET_TONE_OF_VOICE.educator]: {
      id: PRESET_TONE_OF_VOICE.educator,
      title: 'Éducateur',
      description: 'Fournissez un contenu clair et utile pour éduquer votre public.',
    },
  },
  [LANGUAGE.bg]: {
    [PRESET_TONE_OF_VOICE.advisor]: {
      id: PRESET_TONE_OF_VOICE.advisor,
      title: 'Съветник',
      description: 'Установете доверие с експертни съвети.',
    },
    [PRESET_TONE_OF_VOICE.motivator]: {
      id: PRESET_TONE_OF_VOICE.motivator,
      title: 'Мотиватор',
      description: 'Вдъхновете читателите, насочвайки ги към действие.',
    },
    [PRESET_TONE_OF_VOICE.entertainer]: {
      id: PRESET_TONE_OF_VOICE.entertainer,
      title: 'Забавник',
      description: 'Създайте забавен текст, който кара хората да се усмихват.',
    },
    [PRESET_TONE_OF_VOICE.empathetic]: {
      id: PRESET_TONE_OF_VOICE.empathetic,
      title: 'Емпатичен',
      description: 'Покажете грижа и се свържете на по-дълбоко ниво.',
    },
    [PRESET_TONE_OF_VOICE.trendsetter]: {
      id: PRESET_TONE_OF_VOICE.trendsetter,
      title: 'Лидер на тенденции',
      description:
        'Свържете се с потребителите на социални медии в тон, който изглежда като да пишете на приятел.',
    },
    [PRESET_TONE_OF_VOICE.innovator]: {
      id: PRESET_TONE_OF_VOICE.innovator,
      title: 'Инноватор',
      description: 'Споделете нови разработки и идеи по уверен, изискан начин.',
    },
    [PRESET_TONE_OF_VOICE.educator]: {
      id: PRESET_TONE_OF_VOICE.educator,
      title: 'Образовател',
      description: '"Предоставете ясна и полезна информация, за да образовате аудиторията.\n',
    },
  },
  [LANGUAGE.cs]: {
    [PRESET_TONE_OF_VOICE.advisor]: {
      id: PRESET_TONE_OF_VOICE.advisor,
      title: 'Poradce',
      description: 'Získejte důvěryhodnost odbornými radami.',
    },
    [PRESET_TONE_OF_VOICE.motivator]: {
      id: PRESET_TONE_OF_VOICE.motivator,
      title: 'Motivátor',
      description: 'Inspirujte čtenáře k tomu, aby jednali.',
    },
    [PRESET_TONE_OF_VOICE.entertainer]: {
      id: PRESET_TONE_OF_VOICE.entertainer,
      title: 'Zabávač',
      description: 'Vytvářejte zábavné texty, které lidem vykouzlí úsměv.',
    },
    [PRESET_TONE_OF_VOICE.empathetic]: {
      id: PRESET_TONE_OF_VOICE.empathetic,
      title: 'Empatický',
      description: 'Projevte zájem a spojte se na hlubší úrovni.',
    },
    [PRESET_TONE_OF_VOICE.trendsetter]: {
      id: PRESET_TONE_OF_VOICE.trendsetter,
      title: 'Trendsetter',
      description: 'Spojte se s uživateli sociálních médií tónem, který připomíná psaní zpráv p',
    },
    [PRESET_TONE_OF_VOICE.innovator]: {
      id: PRESET_TONE_OF_VOICE.innovator,
      title: 'Inovátor',
      description:
        'Sdílejte nové vývojové trendy a myšlenky sebevědomým a sofistikovaným způsobem.',
    },
    [PRESET_TONE_OF_VOICE.educator]: {
      id: PRESET_TONE_OF_VOICE.educator,
      title: 'Učitel',
      description: 'Poskytněte jasný a užitečný obsah pro vzdělávání publika.',
    },
  },
  [LANGUAGE.da]: {
    [PRESET_TONE_OF_VOICE.advisor]: {
      id: PRESET_TONE_OF_VOICE.advisor,
      title: 'Rådgiver',
      description: 'Etabler troværdighed med ekspertråd.',
    },
    [PRESET_TONE_OF_VOICE.motivator]: {
      id: PRESET_TONE_OF_VOICE.motivator,
      title: 'Motivator',
      description: 'Inspirer læserne og opfordre dem til handling.',
    },
    [PRESET_TONE_OF_VOICE.entertainer]: {
      id: PRESET_TONE_OF_VOICE.entertainer,
      title: 'Underholder',
      description: 'Skab underholdende indhold, der får folk til at smile.',
    },
    [PRESET_TONE_OF_VOICE.empathetic]: {
      id: PRESET_TONE_OF_VOICE.empathetic,
      title: 'Empatisk',
      description: 'Skab underholdende indhold, der får folk til at smile.',
    },
    [PRESET_TONE_OF_VOICE.trendsetter]: {
      id: PRESET_TONE_OF_VOICE.trendsetter,
      title: 'Trendsetter',
      description:
        'Forbind med social media-brugere i en tone, der føles som en besked til en ven.',
    },
    [PRESET_TONE_OF_VOICE.innovator]: {
      id: PRESET_TONE_OF_VOICE.innovator,
      title: 'Innovator',
      description: 'Del nye udviklinger og ideer på en selvsikker og sofistikeret måde.',
    },
    [PRESET_TONE_OF_VOICE.educator]: {
      id: PRESET_TONE_OF_VOICE.educator,
      title: 'Uddanner',
      description: 'Giv klart og nyttigt indhold for at uddanne publikum.',
    },
  },
  [LANGUAGE.nl]: {
    [PRESET_TONE_OF_VOICE.advisor]: {
      id: PRESET_TONE_OF_VOICE.advisor,
      title: 'Adviseur',
      description: 'Vestig geloofwaardigheid met deskundig advies.',
    },
    [PRESET_TONE_OF_VOICE.motivator]: {
      id: PRESET_TONE_OF_VOICE.motivator,
      title: 'Motivator',
      description: 'Inspireer lezers en zet aan tot actie.',
    },
    [PRESET_TONE_OF_VOICE.entertainer]: {
      id: PRESET_TONE_OF_VOICE.entertainer,
      title: 'Entertainer',
      description: 'Creëer leuke teksten die mensen laten glimlachen.',
    },
    [PRESET_TONE_OF_VOICE.empathetic]: {
      id: PRESET_TONE_OF_VOICE.empathetic,
      title: 'Empathisch',
      description: 'Toon zorg en maak een diepere verbinding.',
    },
    [PRESET_TONE_OF_VOICE.trendsetter]: {
      id: PRESET_TONE_OF_VOICE.trendsetter,
      title: 'Trendsetter',
      description: "Verbind met social media-gebruikers in een toon alsof je een vriend sms't.",
    },
    [PRESET_TONE_OF_VOICE.innovator]: {
      id: PRESET_TONE_OF_VOICE.innovator,
      title: 'Innovator',
      description: 'Deel nieuwe ontwikkelingen en ideeën op een zelfverzekerde, verfijnde manier.',
    },
    [PRESET_TONE_OF_VOICE.educator]: {
      id: PRESET_TONE_OF_VOICE.educator,
      title: 'Onderwijzer',
      description: 'Bied duidelijke, nuttige inhoud om het publiek te informeren.',
    },
  },
  [LANGUAGE.hu]: {
    [PRESET_TONE_OF_VOICE.advisor]: {
      id: PRESET_TONE_OF_VOICE.advisor,
      title: 'Tanácsadó',
      description: 'Alapozd meg a hitelességed szakértői tanácsokkal.',
    },
    [PRESET_TONE_OF_VOICE.motivator]: {
      id: PRESET_TONE_OF_VOICE.motivator,
      title: 'Motivátor',
      description: 'Inspirálja az olvasókat, hogy cselekvésre ösztönözze őket.',
    },
    [PRESET_TONE_OF_VOICE.entertainer]: {
      id: PRESET_TONE_OF_VOICE.entertainer,
      title: 'Szórakoztató',
      description: 'Készítsen szórakoztató tartalmat, amely mosolyt csal az emberek arcára.',
    },
    [PRESET_TONE_OF_VOICE.empathetic]: {
      id: PRESET_TONE_OF_VOICE.empathetic,
      title: 'Empatikus',
      description: 'Mutassa meg a törődést, és kapcsolódjon mélyebb szinten.',
    },
    [PRESET_TONE_OF_VOICE.trendsetter]: {
      id: PRESET_TONE_OF_VOICE.trendsetter,
      title: 'Trendszetter',
      description:
        'Kapcsolódj a közösségi média megszállottjaihoz egy olyan hangnemben, ami olyan, mintha egy barátodnak üzenetet írnál.',
    },
    [PRESET_TONE_OF_VOICE.innovator]: {
      id: PRESET_TONE_OF_VOICE.innovator,
      title: 'Innovátor',
      description: 'Ossza meg az új fejlesztéseket és ötleteket magabiztos, kifinomult módon.',
    },
    [PRESET_TONE_OF_VOICE.educator]: {
      id: PRESET_TONE_OF_VOICE.educator,
      title: 'Oktató',
      description: 'Világos és hasznos tartalom biztosítása a közönség oktatására.',
    },
  },
  [LANGUAGE.it]: {
    [PRESET_TONE_OF_VOICE.advisor]: {
      id: PRESET_TONE_OF_VOICE.advisor,
      title: 'Consulente',
      description: 'Stabilire la credibilità con consigli esperti.',
    },
    [PRESET_TONE_OF_VOICE.motivator]: {
      id: PRESET_TONE_OF_VOICE.motivator,
      title: 'Motivatore',
      description: 'Ispirare i lettori, incoraggiandoli all’azione.',
    },
    [PRESET_TONE_OF_VOICE.entertainer]: {
      id: PRESET_TONE_OF_VOICE.entertainer,
      title: 'Intrattenitore',
      description: 'Creare contenuti divertenti che facciano sorridere le persone.',
    },
    [PRESET_TONE_OF_VOICE.empathetic]: {
      id: PRESET_TONE_OF_VOICE.empathetic,
      title: 'Empatico',
      description: 'Mostrare cura e connettersi a un livello più profondo.',
    },
    [PRESET_TONE_OF_VOICE.trendsetter]: {
      id: PRESET_TONE_OF_VOICE.trendsetter,
      title: 'Trendsetter',
      description: 'Condividi nuovi sviluppi e idee in modo sicuro e sofisticato.',
    },
    [PRESET_TONE_OF_VOICE.innovator]: {
      id: PRESET_TONE_OF_VOICE.innovator,
      title: 'Innovatore',
      description: 'Fornisci contenuti chiari e utili per educare il pubblico.',
    },
    [PRESET_TONE_OF_VOICE.educator]: {
      id: PRESET_TONE_OF_VOICE.educator,
      title: 'Educatore',
      description: 'Fornire contenuti chiari e utili per istruire il pubblico.',
    },
  },
  [LANGUAGE.pl]: {
    [PRESET_TONE_OF_VOICE.advisor]: {
      id: PRESET_TONE_OF_VOICE.advisor,
      title: 'Doradca',
      description: 'Zbuduj wiarygodność dzięki fachowym poradom.',
    },
    [PRESET_TONE_OF_VOICE.motivator]: {
      id: PRESET_TONE_OF_VOICE.motivator,
      title: 'Motywator',
      description: 'Zainspiruj czytelników, zachęcając ich do działania.',
    },
    [PRESET_TONE_OF_VOICE.entertainer]: {
      id: PRESET_TONE_OF_VOICE.entertainer,
      title: 'Rozrywkowiec',
      description: 'Twórz zabawne treści, które sprawiają, że ludzie się uśmiechają.',
    },
    [PRESET_TONE_OF_VOICE.empathetic]: {
      id: PRESET_TONE_OF_VOICE.empathetic,
      title: 'Empatyczny',
      description: 'Okazuj troskę i nawiąż głębszą więź.',
    },
    [PRESET_TONE_OF_VOICE.trendsetter]: {
      id: PRESET_TONE_OF_VOICE.trendsetter,
      title: 'Twórca trendów',
      description:
        'Nawiąż kontakt z użytkownikami mediów społecznościowych w tonie przypominającym wiadomości do przyjaciela.',
    },
    [PRESET_TONE_OF_VOICE.innovator]: {
      id: PRESET_TONE_OF_VOICE.innovator,
      title: 'Innowator',
      description: 'Dziel się nowymi pomysłami i rozwiązaniami w pewny, wyrafinowany sposób.',
    },
    [PRESET_TONE_OF_VOICE.educator]: {
      id: PRESET_TONE_OF_VOICE.educator,
      title: 'Edukator',
      description: 'Dostarczaj jasne, pomocne treści, aby edukować odbiorców.',
    },
  },
  [LANGUAGE.pt_pt]: {
    [PRESET_TONE_OF_VOICE.advisor]: {
      id: PRESET_TONE_OF_VOICE.advisor,
      title: 'Consultor',
      description: 'Estabeleça credibilidade com conselhos de especialistas.',
    },
    [PRESET_TONE_OF_VOICE.motivator]: {
      id: PRESET_TONE_OF_VOICE.motivator,
      title: 'Motivador',
      description: 'Inspirar os leitores, levando-os à ação.',
    },
    [PRESET_TONE_OF_VOICE.entertainer]: {
      id: PRESET_TONE_OF_VOICE.entertainer,
      title: 'Animador',
      description: 'Criar conteúdo divertido que faça as pessoas sorrirem.',
    },
    [PRESET_TONE_OF_VOICE.empathetic]: {
      id: PRESET_TONE_OF_VOICE.empathetic,
      title: 'Empático',
      description: 'Mostrar empatia e conectar-se em um nível mais profundo.',
    },
    [PRESET_TONE_OF_VOICE.trendsetter]: {
      id: PRESET_TONE_OF_VOICE.trendsetter,
      title: 'Inovador de tendências',
      description:
        'Conectar-se com nativos das redes sociais em um tom que pareça enviar uma mensagem a um amigo.',
    },
    [PRESET_TONE_OF_VOICE.innovator]: {
      id: PRESET_TONE_OF_VOICE.innovator,
      title: 'Inovador',
      description:
        'Compartilhar novas ideias e desenvolvimentos de maneira confiante e sofisticada.',
    },
    [PRESET_TONE_OF_VOICE.educator]: {
      id: PRESET_TONE_OF_VOICE.educator,
      title: 'Educador',
      description: 'Fornecer conteúdo claro e útil para educar o público.',
    },
  },
  [LANGUAGE.pt_br]: {
    [PRESET_TONE_OF_VOICE.advisor]: {
      id: PRESET_TONE_OF_VOICE.advisor,
      title: 'Consultor',
      description: 'Estabeleça credibilidade com conselhos de especialistas.',
    },
    [PRESET_TONE_OF_VOICE.motivator]: {
      id: PRESET_TONE_OF_VOICE.motivator,
      title: 'Motivador',
      description: 'Inspirar os leitores, levando-os à ação.',
    },
    [PRESET_TONE_OF_VOICE.entertainer]: {
      id: PRESET_TONE_OF_VOICE.entertainer,
      title: 'Animador',
      description: 'Criar conteúdo divertido que faça as pessoas sorrirem.',
    },
    [PRESET_TONE_OF_VOICE.empathetic]: {
      id: PRESET_TONE_OF_VOICE.empathetic,
      title: 'Empático',
      description: 'Mostrar empatia e conectar-se em um nível mais profundo.',
    },
    [PRESET_TONE_OF_VOICE.trendsetter]: {
      id: PRESET_TONE_OF_VOICE.trendsetter,
      title: 'Inovador de tendências',
      description:
        'Conectar-se com nativos das redes sociais em um tom que pareça enviar uma mensagem a um amigo.',
    },
    [PRESET_TONE_OF_VOICE.innovator]: {
      id: PRESET_TONE_OF_VOICE.innovator,
      title: 'Inovador',
      description:
        'Compartilhar novas ideias e desenvolvimentos de maneira confiante e sofisticada.',
    },
    [PRESET_TONE_OF_VOICE.educator]: {
      id: PRESET_TONE_OF_VOICE.educator,
      title: 'Educador',
      description: 'Fornecer conteúdo claro e útil para educar o público.',
    },
  },
  [LANGUAGE.ro]: {
    [PRESET_TONE_OF_VOICE.advisor]: {
      id: PRESET_TONE_OF_VOICE.advisor,
      title: 'Consultant',
      description: 'Stabiliți credibilitatea prin sfaturi de specialitate.',
    },
    [PRESET_TONE_OF_VOICE.motivator]: {
      id: PRESET_TONE_OF_VOICE.motivator,
      title: 'Motivator',
      description: 'Inspirați cititorii și motivați-i să acționeze.',
    },
    [PRESET_TONE_OF_VOICE.entertainer]: {
      id: PRESET_TONE_OF_VOICE.entertainer,
      title: 'Distractor',
      description: 'Creați conținut amuzant care să îi facă pe oameni să zâmbească.',
    },
    [PRESET_TONE_OF_VOICE.empathetic]: {
      id: PRESET_TONE_OF_VOICE.empathetic,
      title: 'Empatic',
      description: 'Arătați grijă și conectați-vă la un nivel mai profund.',
    },
    [PRESET_TONE_OF_VOICE.trendsetter]: {
      id: PRESET_TONE_OF_VOICE.trendsetter,
      title: 'Stabilitor de tendințe',
      description:
        'Conectați-vă cu utilizatorii de social media într-un ton care se simte ca și cum ați trimite un mesaj unui prieten.',
    },
    [PRESET_TONE_OF_VOICE.innovator]: {
      id: PRESET_TONE_OF_VOICE.innovator,
      title: 'Inovator',
      description: 'Împărtășiți noi dezvoltări și idei într-un mod încrezător și sofisticat.',
    },
    [PRESET_TONE_OF_VOICE.educator]: {
      id: PRESET_TONE_OF_VOICE.educator,
      title: 'Educator',
      description: 'Oferiți conținut clar și util pentru a educa publicul.',
    },
  },
  [LANGUAGE.ru]: {
    [PRESET_TONE_OF_VOICE.advisor]: {
      id: PRESET_TONE_OF_VOICE.advisor,
      title: 'Советник',
      description: 'Создайте доверие с помощью экспертных рекомендаций.',
    },
    [PRESET_TONE_OF_VOICE.motivator]: {
      id: PRESET_TONE_OF_VOICE.motivator,
      title: 'Мотиватор',
      description: 'Вдохновляйте читателей, побуждая их к действию.',
    },
    [PRESET_TONE_OF_VOICE.entertainer]: {
      id: PRESET_TONE_OF_VOICE.entertainer,
      title: 'Развлекатель',
      description: 'Создавайте забавный контент, который вызывает улыбку у людей.',
    },
    [PRESET_TONE_OF_VOICE.empathetic]: {
      id: PRESET_TONE_OF_VOICE.empathetic,
      title: 'Эмпатичный',
      description: 'Проявляйте заботу и устанавливайте глубокую связь.',
    },
    [PRESET_TONE_OF_VOICE.trendsetter]: {
      id: PRESET_TONE_OF_VOICE.trendsetter,
      title: 'Лидер мнений',
      description:
        'Общайтесь с пользователями социальных сетей на языке, который звучит как сообщение от друга.',
    },
    [PRESET_TONE_OF_VOICE.innovator]: {
      id: PRESET_TONE_OF_VOICE.innovator,
      title: 'Инноватор',
      description: 'Делитесь новыми разработками и идеями уверенно и изысканно.',
    },
    [PRESET_TONE_OF_VOICE.educator]: {
      id: PRESET_TONE_OF_VOICE.educator,
      title: 'Наставник/Обучающий',
      description: 'Предоставляйте четкий и полезный контент, чтобы обучать свою аудиторию.',
    },
  },
  [LANGUAGE.sv]: {
    [PRESET_TONE_OF_VOICE.advisor]: {
      id: PRESET_TONE_OF_VOICE.advisor,
      title: 'Rådgivare',
      description: 'Bygg förtroende genom professionella råd.',
    },
    [PRESET_TONE_OF_VOICE.motivator]: {
      id: PRESET_TONE_OF_VOICE.motivator,
      title: 'Motivator',
      description: 'Inspirera läsare och motivera till handling.',
    },
    [PRESET_TONE_OF_VOICE.entertainer]: {
      id: PRESET_TONE_OF_VOICE.entertainer,
      title: 'Underhållare',
      description: 'Skapa roliga innehåll som får människor att le.',
    },
    [PRESET_TONE_OF_VOICE.empathetic]: {
      id: PRESET_TONE_OF_VOICE.empathetic,
      title: 'Empatisk',
      description: 'Visa medkänsla och skapa en djupare kontakt.',
    },
    [PRESET_TONE_OF_VOICE.trendsetter]: {
      id: PRESET_TONE_OF_VOICE.trendsetter,
      title: 'Trendsättare',
      description:
        'Anslut med sociala medier-användare med en ton som känns som ett meddelande till en vän.',
    },
    [PRESET_TONE_OF_VOICE.innovator]: {
      id: PRESET_TONE_OF_VOICE.innovator,
      title: 'Innovatör',
      description: 'Dela nya idéer och utvecklingar med självförtroende och elegans.',
    },
    [PRESET_TONE_OF_VOICE.educator]: {
      id: PRESET_TONE_OF_VOICE.educator,
      title: 'Lärare',
      description: 'Ge tydligt och hjälpsamt innehåll för att undervisa publiken.',
    },
  },
  [LANGUAGE.cmn]: {
    [PRESET_TONE_OF_VOICE.advisor]: {
      id: PRESET_TONE_OF_VOICE.advisor,
      title: '顾问',
      description: '通过专家建议建立可信度。',
    },
    [PRESET_TONE_OF_VOICE.motivator]: {
      id: PRESET_TONE_OF_VOICE.motivator,
      title: '鼓舞者',
      description: '鼓舞读者，并推动他们采取行动。',
    },
    [PRESET_TONE_OF_VOICE.entertainer]: {
      id: PRESET_TONE_OF_VOICE.entertainer,
      title: '有趣的人 ',
      description: '创作有趣的内容，让人们会心一笑。',
    },
    [PRESET_TONE_OF_VOICE.empathetic]: {
      id: PRESET_TONE_OF_VOICE.empathetic,
      title: '关怀的人',
      description: '表现关怀，并在更深层次上与他人建立联系。',
    },
    [PRESET_TONE_OF_VOICE.trendsetter]: {
      id: PRESET_TONE_OF_VOICE.trendsetter,
      title: '潮流引领者',
      description: '用像发短信给朋友的语气与社交媒体用户沟通。',
    },
    [PRESET_TONE_OF_VOICE.innovator]: {
      id: PRESET_TONE_OF_VOICE.innovator,
      title: '创新者',
      description: '以自信、成熟的方式分享新发展和创意。',
    },
    [PRESET_TONE_OF_VOICE.educator]: {
      id: PRESET_TONE_OF_VOICE.educator,
      title: '老师',
      description: '提供清晰、有帮助的内容来教育受众。',
    },
  },
  [LANGUAGE.yue]: {
    [PRESET_TONE_OF_VOICE.advisor]: {
      id: PRESET_TONE_OF_VOICE.advisor,
      title: '顧問',
      description: '通过专家建议建立可信度。',
    },
    [PRESET_TONE_OF_VOICE.motivator]: {
      id: PRESET_TONE_OF_VOICE.motivator,
      title: '激勵者',
      description: '激勵讀者，推動他們行動。',
    },
    [PRESET_TONE_OF_VOICE.entertainer]: {
      id: PRESET_TONE_OF_VOICE.entertainer,
      title: '娛樂者',
      description: '創作有趣嘅內容，令人開心笑。',
    },
    [PRESET_TONE_OF_VOICE.empathetic]: {
      id: PRESET_TONE_OF_VOICE.empathetic,
      title: '共情者',
      description: '表現關懷，並與他人建立深層次嘅聯繫。',
    },
    [PRESET_TONE_OF_VOICE.trendsetter]: {
      id: PRESET_TONE_OF_VOICE.trendsetter,
      title: '潮流引領者',
      description: '用好似同朋友講嘢嘅語氣，聯繫社交媒體嘅用戶。',
    },
    [PRESET_TONE_OF_VOICE.innovator]: {
      id: PRESET_TONE_OF_VOICE.innovator,
      title: '創新者',
      description: '以自信、成熟嘅方式分享新發展同創意。',
    },
    [PRESET_TONE_OF_VOICE.educator]: {
      id: PRESET_TONE_OF_VOICE.educator,
      title: '教育者',
      description: '提供清晰、有幫助嘅內容嚟教育受眾。',
    },
  },
  [LANGUAGE.ja]: {
    [PRESET_TONE_OF_VOICE.advisor]: {
      id: PRESET_TONE_OF_VOICE.advisor,
      title: 'アドバイザー',
      description: '専門家のアドバイスで信頼性を確立する。',
    },
    [PRESET_TONE_OF_VOICE.motivator]: {
      id: PRESET_TONE_OF_VOICE.motivator,
      title: 'モチベーター',
      description: '読者をインスパイアし、行動を促す。',
    },
    [PRESET_TONE_OF_VOICE.entertainer]: {
      id: PRESET_TONE_OF_VOICE.entertainer,
      title: 'エンターテイナー',
      description: '人々を笑顔にする楽しいコンテンツを作成する。',
    },
    [PRESET_TONE_OF_VOICE.empathetic]: {
      id: PRESET_TONE_OF_VOICE.empathetic,
      title: '共感的',
      description: 'ケアを示し、より深いつながりを築く。',
    },
    [PRESET_TONE_OF_VOICE.trendsetter]: {
      id: PRESET_TONE_OF_VOICE.trendsetter,
      title: 'トレンドセッター',
      description: 'ソーシャルメディアネイティブとつながる。',
    },
    [PRESET_TONE_OF_VOICE.innovator]: {
      id: PRESET_TONE_OF_VOICE.innovator,
      title: 'イノベーター',
      description: '自信を持って洗練された方法で新しい発展やアイデアを共有する。',
    },
    [PRESET_TONE_OF_VOICE.educator]: {
      id: PRESET_TONE_OF_VOICE.educator,
      title: '教育者',
      description: '観客を教育するために明確で有益なコンテンツを提供する。',
    },
  },
  [LANGUAGE.zh_tw]: {
    [PRESET_TONE_OF_VOICE.advisor]: {
      id: PRESET_TONE_OF_VOICE.advisor,
      title: '顧問',
      description: '以專家建議建立信譽。',
    },
    [PRESET_TONE_OF_VOICE.motivator]: {
      id: PRESET_TONE_OF_VOICE.motivator,
      title: '激勵者',
      description: '激勵讀者並促使他們採取行動。',
    },
    [PRESET_TONE_OF_VOICE.entertainer]: {
      id: PRESET_TONE_OF_VOICE.entertainer,
      title: '娛樂者',
      description: '創造能使人們微笑的有趣內容。',
    },
    [PRESET_TONE_OF_VOICE.empathetic]: {
      id: PRESET_TONE_OF_VOICE.empathetic,
      title: '同理者',
      description: '展現同理心，與受眾建立更深層連結。',
    },
    [PRESET_TONE_OF_VOICE.trendsetter]: {
      id: PRESET_TONE_OF_VOICE.trendsetter,
      title: '潮流引領者',
      description: '以親切語氣與社交媒體用戶互動，彷彿在給朋友發訊息。',
    },
    [PRESET_TONE_OF_VOICE.innovator]: {
      id: PRESET_TONE_OF_VOICE.innovator,
      title: '創新者',
      description: '以安全且精緻的方式分享新發展和創意。',
    },
    [PRESET_TONE_OF_VOICE.educator]: {
      id: PRESET_TONE_OF_VOICE.educator,
      title: '教育者',
      description: '提供清晰且實用的內容以啟發您的受眾。',
    },
  },
  [LANGUAGE.zh_cn]: {
    [PRESET_TONE_OF_VOICE.advisor]: {
      id: PRESET_TONE_OF_VOICE.advisor,
      title: '顾问',
      description: '以专家建议建立信誉。',
    },
    [PRESET_TONE_OF_VOICE.motivator]: {
      id: PRESET_TONE_OF_VOICE.motivator,
      title: '激励者',
      description: '激励读者并促使他们采取行动。',
    },
    [PRESET_TONE_OF_VOICE.entertainer]: {
      id: PRESET_TONE_OF_VOICE.entertainer,
      title: '娱乐者',
      description: '创造能让人们微笑的有趣内容。',
    },
    [PRESET_TONE_OF_VOICE.empathetic]: {
      id: PRESET_TONE_OF_VOICE.empathetic,
      title: '同理者',
      description: '展现同理心，与受众建立更深层次联系。',
    },
    [PRESET_TONE_OF_VOICE.trendsetter]: {
      id: PRESET_TONE_OF_VOICE.trendsetter,
      title: '潮流引领者',
      description: '以亲切语气与社交媒体用户互动，仿佛在给朋友发信息。',
    },
    [PRESET_TONE_OF_VOICE.innovator]: {
      id: PRESET_TONE_OF_VOICE.innovator,
      title: '创新者',
      description: '以安全且精致的方式分享新发展和创意。',
    },
    [PRESET_TONE_OF_VOICE.educator]: {
      id: PRESET_TONE_OF_VOICE.educator,
      title: '教育者',
      description: '提供清晰且实用的内容以启发您的受众。',
    },
  },
  [LANGUAGE.th]: {
    [PRESET_TONE_OF_VOICE.advisor]: {
      id: PRESET_TONE_OF_VOICE.advisor,
      title: 'ที่ปรึกษา',
      description: 'สร้างความน่าเชื่อถือด้วยคำแนะนำจากผู้เชี่ยวชาญ',
    },
    [PRESET_TONE_OF_VOICE.motivator]: {
      id: PRESET_TONE_OF_VOICE.motivator,
      title: 'ผู้สร้างแรงบันดาลใจ',
      description: 'สร้างแรงบันดาลใจให้ผู้อ่านและกระตุ้นให้พวกเขาลงมือทำ',
    },
    [PRESET_TONE_OF_VOICE.entertainer]: {
      id: PRESET_TONE_OF_VOICE.entertainer,
      title: 'นักบันเทิง',
      description: 'สร้างเนื้อหาที่สนุกสนานและทำให้ผู้คนยิ้มได้',
    },
    [PRESET_TONE_OF_VOICE.empathetic]: {
      id: PRESET_TONE_OF_VOICE.empathetic,
      title: 'ผู้เห็นอกเห็นใจ',
      description: 'แสดงความเห็นอกเห็นใจและเชื่อมต่อกับผู้อ่านในระดับที่ลึกซึ้งยิ่งขึ้น',
    },
    [PRESET_TONE_OF_VOICE.trendsetter]: {
      id: PRESET_TONE_OF_VOICE.trendsetter,
      title: 'ผู้นำเทรนด์',
      description:
        'เชื่อมต่อกับผู้ใช้โซเชียลมีเดียด้วยโทนเสียงที่เป็นกันเอง ราวกับส่งข้อความถึงเพื่อน',
    },
    [PRESET_TONE_OF_VOICE.innovator]: {
      id: PRESET_TONE_OF_VOICE.innovator,
      title: 'นักนวัตกรรม',
      description: 'แบ่งปันพัฒนาการใหม่ๆ และไอเดียอย่างปลอดภัยและมีความประณีต',
    },
    [PRESET_TONE_OF_VOICE.educator]: {
      id: PRESET_TONE_OF_VOICE.educator,
      title: 'ผู้สอน',
      description: 'ให้เนื้อหาที่ชัดเจนและมีประโยชน์เพื่อให้ความรู้แก่ผู้ชม',
    },
  },
  [LANGUAGE.ms]: {
    [PRESET_TONE_OF_VOICE.advisor]: {
      id: PRESET_TONE_OF_VOICE.advisor,
      title: 'Penasihat',
      description: 'Wujudkan kredibiliti dengan nasihat pakar.',
    },
    [PRESET_TONE_OF_VOICE.motivator]: {
      id: PRESET_TONE_OF_VOICE.motivator,
      title: 'Penyemangat',
      description: 'Berikan inspirasi kepada pembaca dan galakkan mereka untuk bertindak.',
    },
    [PRESET_TONE_OF_VOICE.entertainer]: {
      id: PRESET_TONE_OF_VOICE.entertainer,
      title: 'Penghibur',
      description: 'Cipta kandungan yang menghiburkan sehingga membuatkan orang tersenyum.',
    },
    [PRESET_TONE_OF_VOICE.empathetic]: {
      id: PRESET_TONE_OF_VOICE.empathetic,
      title: 'Empatik',
      description: 'Tunjukkan empati dan berhubung pada tahap yang lebih mendalam.',
    },
    [PRESET_TONE_OF_VOICE.trendsetter]: {
      id: PRESET_TONE_OF_VOICE.trendsetter,
      title: 'Peneraju Trend',
      description:
        'Berhubung dengan pengguna media sosial dengan nada mesra, seolah-olah menghantar mesej kepada seorang kawan.',
    },
    [PRESET_TONE_OF_VOICE.innovator]: {
      id: PRESET_TONE_OF_VOICE.innovator,
      title: 'Inovator',
      description: 'Kongsi pembangunan dan idea baru dengan cara yang selamat dan canggih.',
    },
    [PRESET_TONE_OF_VOICE.educator]: {
      id: PRESET_TONE_OF_VOICE.educator,
      title: 'Pendidik',
      description: 'Sediakan kandungan yang jelas dan berguna untuk mendidik audien anda.',
    },
  },
  [LANGUAGE.ko]: {
    [PRESET_TONE_OF_VOICE.advisor]: {
      id: PRESET_TONE_OF_VOICE.advisor,
      title: '조언자',
      description: '전문가의 조언으로 신뢰성을 확립하세요.',
    },
    [PRESET_TONE_OF_VOICE.motivator]: {
      id: PRESET_TONE_OF_VOICE.motivator,
      title: '동기부여자',
      description: '독자들에게 영감을 주고 행동을 촉구하세요.',
    },
    [PRESET_TONE_OF_VOICE.entertainer]: {
      id: PRESET_TONE_OF_VOICE.entertainer,
      title: '엔터테이너',
      description: '사람들이 미소 짓게 만드는 재미있는 콘텐츠를 만드세요.',
    },
    [PRESET_TONE_OF_VOICE.empathetic]: {
      id: PRESET_TONE_OF_VOICE.empathetic,
      title: '공감하는',
      description: '관심을 보이고 더 깊은 수준에서 연결하세요.',
    },
    [PRESET_TONE_OF_VOICE.trendsetter]: {
      id: PRESET_TONE_OF_VOICE.trendsetter,
      title: '트렌드 세터',
      description:
        '소셜 미디어 네이티브들과 친근한 톤으로 연결하세요. 마치 친구에게 메시지를 보내는 것처럼.',
    },
    [PRESET_TONE_OF_VOICE.innovator]: {
      id: PRESET_TONE_OF_VOICE.innovator,
      title: '혁신가',
      description: '새로운 발전과 아이디어를 안전하고 정교한 방식으로 공유하세요.',
    },
    [PRESET_TONE_OF_VOICE.educator]: {
      id: PRESET_TONE_OF_VOICE.educator,
      title: '교육자',
      description: '청중을 교육하기 위한 명확하고 유용한 콘텐츠를 제공하세요.',
    },
  },
  [HIDDEN_LANGUAGE.he]: {
    [PRESET_TONE_OF_VOICE.advisor]: {
      id: PRESET_TONE_OF_VOICE.advisor,
      title: 'יועץ',
      description: 'בסס אמינות באמצעות מתן עצות מומחה',
    },
    [PRESET_TONE_OF_VOICE.motivator]: {
      id: PRESET_TONE_OF_VOICE.motivator,
      title: 'מעורר מוטיבציה',
      description: 'הענק השראה לקוראים ועודד אותם לפעולה',
    },
    [PRESET_TONE_OF_VOICE.entertainer]: {
      id: PRESET_TONE_OF_VOICE.entertainer,
      title: 'בדרן',
      description: 'צור תוכן מהנה ומשעשע שיגרום לאנשים לחייך',
    },
    [PRESET_TONE_OF_VOICE.empathetic]: {
      id: PRESET_TONE_OF_VOICE.empathetic,
      title: 'אמפתי',
      description: 'הראה אכפתיות והתחבר עם הקוראים',
    },
    [PRESET_TONE_OF_VOICE.trendsetter]: {
      id: PRESET_TONE_OF_VOICE.trendsetter,
      title: 'מוביל טרנדים',
      description: 'צור חיבור עם קוראים מדור ה-Z',
    },
    [PRESET_TONE_OF_VOICE.innovator]: {
      id: PRESET_TONE_OF_VOICE.innovator,
      title: 'פורץ דרך',
      description: 'שתף בהתפתחויות ורעיונות חדשים באופן מתוחכם ומלא בטחון',
    },
    [PRESET_TONE_OF_VOICE.educator]: {
      id: PRESET_TONE_OF_VOICE.educator,
      title: 'מחנך',
      description: 'ספק תוכן ברור ומועיל כדי להעשיר את הידע של הקהל שלך',
    },
  },
};

export const DEFAULT_SUGGEST_TOV_MODE = PRESET_TONE_OF_VOICE.engaging;

export const TOV_INPUT_TYPE = {
  samples: 'samples',
  guidelines: 'guidelines',
} as const;
export type TovInputType = (typeof TOV_INPUT_TYPE)[keyof typeof TOV_INPUT_TYPE];

export const DRAFT_TOV_ID = 'draft_tov';

export type SelectedTov = PresetToneOfVoice | ID | typeof DRAFT_TOV_ID;

export const OTHER_TEST_OPTION_ID = 'other-option';

export const TOV_TEST_OPTION = {
  fbPostWebinar: 'fb-post-webinar',
  liPostRenewable: 'li-post-renewable-',
  liPostWorkplace: 'li-post-workplace-',
  emailFeedbackSurvey: 'email-feedback-survey-',
  emailFeatureAnnouncement: 'email-feature-announcement',
  otherOption: OTHER_TEST_OPTION_ID,
} as const;
export type TovTestOption = (typeof TOV_TEST_OPTION)[keyof typeof TOV_TEST_OPTION];

interface TovTestOptionProps {
  title: string;
  text: string;
  generationTool: GenerationTool;
}

export const TOV_TEST_OPTION_PROPS: Record<TovTestOption, TovTestOptionProps> = {
  [TOV_TEST_OPTION.fbPostWebinar]: {
    title: 'Facebook Post - Webinar Invitation',
    text: `Join ACME Corp's exclusive webinar! Discover the latest industry trends, expert insights, and actionable strategies to elevate your business.\nLearn from field leaders and network with like-minded professionals. Our experts will share knowledge on the latest advancements and how to apply them for success.\nRegister now to secure your spot and gain valuable insights. Visit our website for details. We look forward to seeing you there!`,
    generationTool: GENERATION_TOOL.facebookPost,
  },
  [TOV_TEST_OPTION.liPostRenewable]: {
    title: 'LinkedIn Post - Renewable Energy',
    text: `Exciting news! ACME Corp’s new community solar projects and renewable energy agreements are transforming 11 states. These projects will add clean energy to local grids, create jobs, support rural areas, and advance ACME Corp’s renewable targets.\nOur portfolio includes over 600 renewable energy projects across 10 countries. Learn how these initiatives will deliver savings to customers and enhance America’s energy grid. Find out more: [Insert link]`,
    generationTool: GENERATION_TOOL.linkedinPost,
  },
  [TOV_TEST_OPTION.liPostWorkplace]: {
    title: 'LinkedIn Post - Best Work Place',
    text: `We are thrilled to announce that AcmeCorp has been named one of Glassdoor's Best Places to Work for 2024! This honor reflects our team's dedication and the inclusive, supportive work environment we've built. We're grateful to every member of our organization for their contributions. Thank you to Glassdoor and our incredible team. Here's to continued success and growth!`,
    generationTool: GENERATION_TOOL.linkedinPost,
  },
  [TOV_TEST_OPTION.emailFeedbackSurvey]: {
    title: 'Feedback Survey Email',
    text: `Dear [Customer Name],\nAt Acme Corp., we strive to provide exceptional experiences for customers like you. We would greatly appreciate your feedback through a short, 5-minute survey.\nYour honest responses will help us improve our products/services. The survey is anonymous, and your participation will make a real difference.\nClick here to take the survey: [Survey Link]\nThank you for your time and support!\nSincerely,\nThe Acme Corp. Team`,
    generationTool: GENERATION_TOOL.emailBody,
  },
  [TOV_TEST_OPTION.emailFeatureAnnouncement]: {
    title: 'Feature Announcement Email',
    text: `Dear [Customer Name],\nWe're excited to introduce a new feature that enhances your Acme Corp experience! Our latest update includes advanced data analytics for deeper insights and informed decisions. Enjoy real-time analysis and customizable reporting tailored to your needs.\nWe believe this innovation will boost your productivity and streamline workflows. Log in to explore this new feature today!\nThank you for your continued support.\nBest regards,\nThe Acme Corp Team`,
    generationTool: GENERATION_TOOL.emailBody,
  },
  [TOV_TEST_OPTION.otherOption]: {
    title: 'Other',
    text: '',
    generationTool: GENERATION_TOOL.freestyle,
  },
};

export const TOV_TEST_NON_ENGLISH_OPTION_PROPS: Record<
  string,
  Record<string, TovTestOptionProps>
> = {
  [LANGUAGE.de]: {
    [TOV_TEST_OPTION.fbPostWebinar]: {
      title: 'Webinar-Einladung (Facebook-Post)',
      text: 'Nehmen Sie am exklusiven Webinar von ACME Corp teil! Entdecken Sie die neuesten Branchentrends, Experteneinsichten und umsetzbare Strategien, um Ihr Geschäft voranzubringen. Lernen Sie von Branchenführern und knüpfen Sie Kontakte mit Gleichgesinnten. Unsere Experten teilen ihr Wissen über die neuesten Entwicklungen und wie Sie diese erfolgreich anwenden können. Melden Sie sich jetzt an, um sich Ihren Platz zu sichern und wertvolle Einblicke zu gewinnen. Besuchen Sie unsere Website für weitere Informationen. Wir freuen uns auf Ihre Teilnahme!',
      generationTool: GENERATION_TOOL.facebookPost,
    },
    [TOV_TEST_OPTION.liPostRenewable]: {
      title: 'Erneuerbare Energien (LinkedIn-Post)',
      text: 'Spannende Neuigkeiten! Die neuen Solarprojekte von ACME Corp und die Vereinbarungen über erneuerbare Energien verändern 11 Bundesstaaten. Diese Projekte bringen saubere Energie in die lokalen Netze, schaffen Arbeitsplätze, unterstützen ländliche Gebiete und fördern die Erreichung der Ziele von ACME Corp im Bereich erneuerbare Energien. Unser Portfolio umfasst über 600 Projekte für erneuerbare Energien in 10 Ländern. Erfahren Sie, wie diese Initiativen Einsparungen für Kunden bringen und das amerikanische Energienetz verbessern werden. Mehr erfahren: [Link einfügen]',
      generationTool: GENERATION_TOOL.linkedinPost,
    },
    [TOV_TEST_OPTION.liPostWorkplace]: {
      title: 'Beste Arbeitsplätze (LinkedIn-Post)',
      text: 'Wir freuen uns sehr, bekannt zu geben, dass ACME Corp zu einem der besten Arbeitgeber auf Glassdoor für 2024 ernannt wurde! Diese Auszeichnung spiegelt das Engagement unseres Teams und die integrative, unterstützende Arbeitsumgebung wider, die wir geschaffen haben. Wir sind jedem Mitglied unseres Unternehmens dankbar für seine Beiträge. Vielen Dank an Glassdoor und unser fantastisches Team. Auf weiterhin viel Erfolg und Wachstum!',
      generationTool: GENERATION_TOOL.linkedinPost,
    },
    [TOV_TEST_OPTION.emailFeedbackSurvey]: {
      title: 'Feedback-Umfrage (E-Mail)',
      text: 'Sehr geehrte/r [Kundenname], bei ACME Corp setzen wir alles daran, Ihnen außergewöhnliche Erlebnisse zu bieten. Wir würden uns sehr über Ihr Feedback durch eine kurze, 5-minütige Umfrage freuen. Ihre ehrlichen Antworten helfen uns, unsere Produkte/Dienstleistungen zu verbessern. Die Umfrage ist anonym und Ihre Teilnahme wird einen echten Unterschied machen. Klicken Sie hier, um an der Umfrage teilzunehmen: [Umfragelink] Vielen Dank für Ihre Zeit und Unterstützung! Mit freundlichen Grüßen, Ihr ACME Corp-Team',
      generationTool: GENERATION_TOOL.emailBody,
    },
    [TOV_TEST_OPTION.emailFeatureAnnouncement]: {
      title: 'Funktionsankündigung (E-Mail)',
      text: 'Sehr geehrte/r [Kundenname], wir freuen uns, Ihnen eine neue Funktion vorzustellen, die Ihr ACME Corp-Erlebnis verbessert! Unser neuestes Update umfasst erweiterte Datenanalysen für tiefere Einblicke und fundierte Entscheidungen. Genießen Sie Echtzeit-Analysen und anpassbare Berichte, die auf Ihre Bedürfnisse zugeschnitten sind. Wir glauben, dass diese Innovation Ihre Produktivität steigern und Arbeitsabläufe optimieren wird. Melden Sie sich noch heute an, um diese neue Funktion zu entdecken! Vielen Dank für Ihre anhaltende Unterstützung. Mit freundlichen Grüßen, Ihr ACME Corp-Team',
      generationTool: GENERATION_TOOL.emailBody,
    },
  },
  [LANGUAGE.de_ch]: {
    [TOV_TEST_OPTION.fbPostWebinar]: {
      title: 'Facebook-Post – Webinar-Einladung (Schweizerdeutsch)',
      text: 'Mached mit bim exklusive Webinar vo ACME Corp! Entdecket d’neuste Branche-Trends, d’Expertetipps und Strategie zum Erfolgricher wärde. Lärnet vo de Branchen-Leiter und vernetzed euch mit Glichgesinnte. Üsi Experten werded ihr Wisso teile zu de neuschte Entwicklige und wie mer die Erfolgrich aawendet. Meldet eu jetzt aa, um eu Platz z’sichere und wertvolli Insichte z’bekomme. Besuechet üsi Website für meh Info. Mir freue eus, eu z’gseh!',
      generationTool: GENERATION_TOOL.facebookPost,
    },
    [TOV_TEST_OPTION.liPostRenewable]: {
      title: 'LinkedIn-Post – Erneuerbari Energie (Schweizerdeutsch)',
      text: 'Ufregendi News! D’neue Solar-Projekte und d’Vertrag für erneuerbari Energie vo ACME Corp verändered 11 Kantone. Die Projekte bringed sauberi Energie in d’ lokale Netz, schaffed Jobs, unterstützed ländligi Gebiet und fördered d’Ziele vo ACME Corp für erneuerbari Energie. Üses Portfolio het über 600 erneuerbari Energie-Projekte i 10 Länder. Lernet meh über die Initiativen und wie die d’Kosten für Chunde senked und d’Energienetz verbessere. Meh erfahre: [Link iifüege]',
      generationTool: GENERATION_TOOL.linkedinPost,
    },
    [TOV_TEST_OPTION.liPostWorkplace]: {
      title: 'LinkedIn-Post – Bester Arbeitsplatz (Schweizerdeutsch)',
      text: 'Mir sind überglücklich z’müelde, dass ACME Corp als eine vo de beschte Arbeitsplätz uf Glassdoor für 2024 gwählt worde isch! Die Ehr würdigt s’Engagement vo üsem Team und das integrativi und unterstützend Arbeitsumfeld, wo mir erschaffe hend. Mir sind allne Mitgliider vo üserer Organisation dankbar für ihre Beiträge. Merci vielmal a Glassdoor und üses super Team. Uf weiterhin grossi Erfolg und Wachschtum!',
      generationTool: GENERATION_TOOL.linkedinPost,
    },
    [TOV_TEST_OPTION.emailFeedbackSurvey]: {
      title: 'E-Mail – Feedback Umfrag (Schweizerdeutsch)',
      text: 'Liebi/r [Name], Bi ACME Corp gebed mir eus Müeh, um eusi Chunde wie dich i jeder Hinsichet z’zufriede z’stelle. Mir wärde eus sehr freue, wenn du d’Zyt würdsch nehme für e chliini 5-Minüte Umfrag. Dini ehrliche Antworte würded eus hälfe, üsi Produkt/Dienstleistige z’verbessere. D’Umfrag isch anonym, und dini Teilnahme macht würkli e Unterschied. Klicke hie, um d’Umfrag z’mache: [Umfrag-Link] Merci vielmol für dini Zyt und Unterstützig! Liebi Grüess, s’Team vo ACME Corp',
      generationTool: GENERATION_TOOL.emailBody,
    },
    [TOV_TEST_OPTION.emailFeatureAnnouncement]: {
      title: 'E-Mail – Funktionä-Update (Schweizerdeutsch)',
      text: 'Liebi/r [Name], mir sind total erfreut, dir e neus Feature vorzstelle, wo dini ACME Corp Erfahrung no besser macht! Üsi neuschti Update enthält fortschrittligi Date-Analyse für tüefere Insichte und informierti Entscheide. Gniess Echtzyt-Analysen und personalisierbari Bericht, die uf dini Bedürftigkeite abgestimmt sind. Mir glaube, dass die Innovatio dini Produktivität stärkt und d’Arbeitsabläuf optimiert. Logge di sofort i, um das neus Feature z’entdecke! Merci vielmol für dini steti Unterstützig. Liebi Grüess, s’Team vo ACME Corp',
      generationTool: GENERATION_TOOL.emailBody,
    },
  },
  [LANGUAGE.el]: {
    [TOV_TEST_OPTION.fbPostWebinar]: {
      title: 'Δημοσίευση στο Facebook - Πρόσκληση σε webinar',
      text: `Συμμετάσχετε σ��ο αποκλειστικό webinar της ACME Corp! Ανακαλύψτε τις τελευταίες τάσεις του κλάδου, πολύτιμες πληροφορίες από ειδικούς και πρακτικές στρατηγικές για την ανάπτυξη της επιχείρησής σας. Μάθετε από κορυφαίους ειδικούς και δικτυωθείτε με άλλους επαγγελματίες. Οι ειδικοί μας θα μοιραστούν τις γνώσεις τους για τις τελευταίες εξελίξεις και πώς να τις εφαρμόσετε για επιτυχία. Κάντε εγγραφή τώρα για να εξασφαλίσετε τη θέση σας και να αποκομίσετε πολύτιμες πληροφορίες. Επισκεφθείτε την ιστοσελίδα μας για λεπτομέρειες. Ανυπομονούμε να σας δούμε εκεί!`,
      generationTool: GENERATION_TOOL.facebookPost,
    },
    [TOV_TEST_OPTION.liPostRenewable]: {
      title: 'Δημοσίευση στο LinkedIn - Ανανεώσιμες πηγές ενέργειας',
      text: `Συναρπαστικά νέα! Τα νέα ηλιακά έργα κοινότητας και οι συμφωνίες ανανεώσιμης ενέργειας της ACME Corp μετασχηματίζουν 11 πολιτείες. Αυτά τα έργα θα προσθέσουν καθαρή ενέργεια στα τοπικά δίκτυα, θα δημιουργήσουν θέσεις εργασίας, θα υποστηρίξουν αγροτικές περιοχές και θα προωθήσουν τους στόχους ανανεώσιμης ενέργειας της ACME Corp. Το χαρτοφυλάκιό μας περιλαμβάνει πάνω από 600 έργα ανανεώσιμης ενέργειας σε 10 χώρες. Μάθετε πώς αυτές οι πρωτοβουλίες θα προσφέρουν εξοικονόμηση στους πελάτες και θα ενισχύσουν το ενεργειακό δίκτυο της Αμερικής. Μάθετε περισσότερα: [Εισαγωγή συνδέσμου]`,
      generationTool: GENERATION_TOOL.linkedinPost,
    },
    [TOV_TEST_OPTION.liPostWorkplace]: {
      title: 'Δημοσίευση στο LinkedIn - Καλύτερος εργοδότης',
      text: `Είμαστε ενθουσιασμένοι να ανακοινώσουμε ότι η ACME Corp έχει αναγνωριστεί ως ένας από τους Καλύτερους Χώρους Εργασίας για το 2024 από το Glassdoor! Αυτή η διάκριση αντικατοπτρίζει την αφοσίωση της ομάδας μας και το περιβάλλον εργασίας που προάγει τη συμπερίληψη και την υποστήριξη. Είμαστε ευγνώμονες σε κάθε μέλος της οργάνωσής μας για τη συνεισφορά του. Ευχαριστούμε το Glassdoor και την απίστευτη ομάδα μας. Στην επιτυχία και την ανάπτυξη που συνεχίζονται!`,
      generationTool: GENERATION_TOOL.linkedinPost,
    },
    [TOV_TEST_OPTION.emailFeedbackSurvey]: {
      title: 'Email - Έρευνα ανατροφοδότησης',
      text: `Αγαπητέ/ή [Όνομα πελάτη], Στην ACME Corp, προσπαθούμε να παρέχουμε εξαιρετικές εμπειρίες για πελάτες σαν εσάς. Θα εκτιμούσαμε πολύ την ανατροφοδότησή σας μέσω μιας σύντομης έρευνας διάρκειας 5 λεπτών. Οι ειλικρινείς απαντήσεις σας θα μας βοηθήσουν να βελτιώσουμε τα προϊόντα/υπηρεσίες μας. Η έρευνα είναι ανώνυμη, και η συμμετοχή σας θα κάνει τη διαφορά. Κάντε κλικ εδώ για να απαντήσετε στην έρευνα: [Σύνδεσμος Έρευνας] Σας ευχαριστούμε για τον χρόνο και την υποστήριξή σας! Με εκτίμηση, Η ομάδα της ACME Corp`,
      generationTool: GENERATION_TOOL.emailBody,
    },
    [TOV_TEST_OPTION.emailFeatureAnnouncement]: {
      title: 'Email - Ανακοίνωση νέας λειτουργίας',
      text: `Αγαπητέ/ή [Όνομα πελάτη], Είμαστε ενθουσιασμένοι να σας παρουσιάσουμε μια νέα λειτουργία που ενισχύει την εμπειρία σας με την ACME Corp! Η τελευταία μας ενημέρωση περιλαμβάνει προηγμένες αναλύσεις δεδομένων για βαθύτερα insights και καλύτερες αποφάσεις. Απολαύστε αναλύσεις σε πραγματικό χρόνο και προσαρμόσιμες αναφορές, σχεδιασμένες για τις ανάγκες σας. Πιστεύουμε ότι αυτή η καινοτομία θα αυξήσει την παραγωγικότητά σας και θα βελτιστοποιήσει τις ροές εργασίας σας. Συνδεθείτε για να εξερευνήσετε αυτή τη νέα λειτουργία σήμερα! Σας ευχαριστούμε για τη συνεχή υποστήριξή σας. Με εκτίμηση, Η ομάδα της ACME Corp`,
      generationTool: GENERATION_TOOL.emailBody,
    },
  },
  [LANGUAGE.es]: {
    [TOV_TEST_OPTION.fbPostWebinar]: {
      title: 'Publicación de Facebook - Invitación a Webinar',
      text: `"¡Únete al webinar exclusivo de ACME Corp! Descubre las últimas tendencias de la industria, perspectivas de expertos y estrategias prácticas para hacer crecer tu negocio. Aprende de líderes del sector y conecta con profesionales afines. Nuestros expertos compartirán conocimientos sobre los últimos avances y cómo aplicarlos para el éxito. Regístrate ahora para asegurar tu lugar y obtener valiosa información. Visita nuestro sitio web para más detalles. ¡Te esperamos!"`,
      generationTool: GENERATION_TOOL.facebookPost,
    },
    [TOV_TEST_OPTION.liPostRenewable]: {
      title: 'Publicación de LinkedIn - Energía Renovable',
      text: `"¡Noticias emocionantes! Los nuevos proyectos solares comunitarios y acuerdos de energía renovable de ACME Corp están transformando 11 estados. Estos proyectos agregarán energía limpia a las redes locales, crearán empleos, apoyarán áreas rurales y avanzarán en los objetivos de energía renovable de ACME Corp. Nuestro portafolio incluye más de 600 proyectos de energía renovable en 10 países. Conoce cómo estas iniciativas generarán ahorros para los clientes y mejorarán la red energética de EE. UU. Más información: [Insertar enlace]"`,
      generationTool: GENERATION_TOOL.linkedinPost,
    },
    [TOV_TEST_OPTION.liPostWorkplace]: {
      title: 'Publicación de LinkedIn - Mejor Lugar de Trabajo',
      text: `¡Estamos encantados de anunciar que ACME Corp ha sido nombrada como uno de los Mejores Lugares para Trabajar en 2024 según Glassdoor! Este honor refleja la dedicación de nuestro equipo y el entorno de trabajo inclusivo y solidario que hemos construido. Agradecemos a cada miembro de nuestra organización por sus contribuciones. Gracias a Glassdoor y a nuestro increíble equipo. ¡Por un éxito y crecimiento continuos!"`,
      generationTool: GENERATION_TOOL.linkedinPost,
    },
    [TOV_TEST_OPTION.emailFeedbackSurvey]: {
      title: 'Correo Electrónico de Encuesta de Opinión',
      text: 'Estimado/a [Nombre del Cliente], en ACME Corp nos esforzamos por ofrecer experiencias excepcionales para clientes como usted. Apreciaríamos mucho su opinión a través de una breve encuesta de 5 minutos. Sus respuestas sinceras nos ayudarán a mejorar nuestros productos/servicios. La encuesta es anónima y su participación marcará una verdadera diferencia. Haga clic aquí para completar la encuesta: [Enlace de Encuesta] ¡Gracias por su tiempo y apoyo! Atentamente, El Equipo de ACME Corp',
      generationTool: GENERATION_TOOL.emailBody,
    },
    [TOV_TEST_OPTION.emailFeatureAnnouncement]: {
      title: 'Correo Electrónico de Anuncio de Función',
      text: 'Estimado/a [Nombre del Cliente], ¡estamos emocionados de presentar una nueva función que mejorará su experiencia con ACME Corp! Nuestra última actualización incluye análisis de datos avanzados para obtener información más detallada y tomar decisiones informadas. Disfrute de análisis en tiempo real y reportes personalizables adaptados a sus necesidades. Creemos que esta innovación aumentará su productividad y optimizará sus flujos de trabajo. Inicie sesión para explorar esta nueva función hoy mismo. Gracias por su continuo apoyo. Saludos, El Equipo de ACME Corp',
      generationTool: GENERATION_TOOL.emailBody,
    },
  },
  [LANGUAGE.es_la]: {
    [TOV_TEST_OPTION.fbPostWebinar]: {
      title: 'Publicación en Facebook - Invitación al webinar',
      text: `¡Únete al webinar exclusivo de ACME Corp! Descubre las últimas tendencias de la industria, perspectivas de expertos y estrategias prácticas para llevar tu negocio al siguiente nivel. Aprende de líderes del sector y conecta con profesionales afines. Nuestros expertos compartirán conocimientos sobre los avances más recientes y cómo aplicarlos para alcanzar el éxito. Regístrate ahora para asegurar tu lugar y obtener información valiosa. Visita nuestra página web para más detalles. ¡Te esperamos!`,
      generationTool: GENERATION_TOOL.facebookPost,
    },
    [TOV_TEST_OPTION.liPostRenewable]: {
      title: 'Publicación en LinkedIn - Energías renovables',
      text: `¡Buenas noticias! Los nuevos proyectos solares comunitarios y acuerdos de energía renovable de ACME Corp están transformando 11 estados. Estos proyectos sumarán energía limpia a las redes locales, crearán empleos, apoyar��n áreas rurales y contribuirán a los objetivos de energías renovables de ACME Corp. Nuestro portafolio incluye más de 600 proyectos de energía renovable en 10 países. Descubre cómo estas iniciativas generarán ahorros para los clientes y mejorarán la red energética de América. Más información aquí: [Inserta el enlace]`,
      generationTool: GENERATION_TOOL.linkedinPost,
    },
    [TOV_TEST_OPTION.liPostWorkplace]: {
      title: 'Publicación en LinkedIn - Mejor lugar para trabajar',
      text: `¡Estamos emocionados de anunciar que ACME Corp ha sido reconocida como uno de los Mejores Lugares para Trabajar de Glassdoor en 2024! Este honor refleja la dedicación de nuestro equipo y el entorno de trabajo inclusivo y de apoyo que hemos construido. Estamos agradecidos con cada miembro de nuestra organización por sus contribuciones. Gracias a Glassdoor y a nuestro increíble equipo. ¡Por más éxitos y crecimiento continuo!`,
      generationTool: GENERATION_TOOL.linkedinPost,
    },
    [TOV_TEST_OPTION.emailFeedbackSurvey]: {
      title: 'Email - Encuesta de satisfacción',
      text: `Estimado/a [Nombre del cliente], En ACME Corp nos esforzamos por brindar experiencias excepcionales a clientes como usted. Agradeceríamos mucho que nos compartiera su opinión a través de una breve encuesta de 5 minutos. Sus respuestas sinceras nos ayudarán a mejorar nuestros productos/servicios. La encuesta es anónima y su participación marcará una gran diferencia. Haga clic aquí para responder a la encuesta: [Enlace de la encuesta] ¡Gracias por su tiempo y apoyo! Atentamente, El equipo de ACME Corp`,
      generationTool: GENERATION_TOOL.emailBody,
    },
    [TOV_TEST_OPTION.emailFeatureAnnouncement]: {
      title: 'Email - Anuncio de nueva funcionalidad',
      text: `Estimado/a [Nombre del cliente], ¡Estamos emocionados de presentar una nueva funcionalidad que mejorará su experiencia con ACME Corp! Nuestra última actualización incluye análisis avanzados de datos para obtener insights más profundos y tomar decisiones informadas. Disfrute de análisis en tiempo real y reportes personalizables adaptados a sus necesidades. Creemos que esta innovación aumentará su productividad y optimizará sus flujos de trabajo. Inicie sesión para explorar esta nueva funcionalidad hoy mismo. ¡Gracias por su continuo apoyo! Atentamente, El equipo de ACME Corp`,
      generationTool: GENERATION_TOOL.emailBody,
    },
  },
  [LANGUAGE.es_cs]: {
    [TOV_TEST_OPTION.fbPostWebinar]: {
      title: 'Publicación de Facebook - Invitación al webinar',
      text: `¡Únete al webinar exclusivo de ACME Corp! Descubre las últimas tendencias del sector, perspectivas de expertos y estrategias prácticas para impulsar tu negocio. Aprende de líderes del sector y conecta con profesionales afines. Nuestros expertos compartirán conocimientos sobre los avances más recientes y cómo aplicarlos para el éxito. Regístrate ahora para asegurar tu plaza y obtener información valiosa. Visita nuestra web para más detalles. ¡Esperamos verte allí!`,
      generationTool: GENERATION_TOOL.facebookPost,
    },
    [TOV_TEST_OPTION.liPostRenewable]: {
      title: 'Publicación de LinkedIn - Energía renovable',
      text: `¡Noticias emocionantes! Los nuevos proyectos solares comunitarios y acuerdos de energía renovable de ACME Corp están transformando 11 estados. Estos proyectos añadirán energía limpia a las redes locales, crearán empleos, apoyarán áreas rurales y avanzarán los objetivos de ACME Corp en energías renovables. Nuestro portafolio incluye más de 600 proyectos de energía renovable en 10 países. Descubre cómo estas iniciativas aportarán ahorros a los clientes y mejorarán la red energética de Estados Unidos. Más información aquí: [Inserta enlace]`,
      generationTool: GENERATION_TOOL.linkedinPost,
    },
    [TOV_TEST_OPTION.liPostWorkplace]: {
      title: 'Publicación de LinkedIn - Mejor lugar de trabajo',
      text: `¡Estamos encantados de anunciar que ACME Corp ha sido nombrada uno de los Mejores Lugares para Trabajar de Glassdoor en 2024! Este reconocimiento refleja la dedicación de nuestro equipo y el entorno laboral inclusivo y de apoyo que hemos construido. Estamos agradecidos con cada miembro de nuestra organización por sus contribuciones. Gracias a Glassdoor y a nuestro increíble equipo. ¡Por un futuro de éxito y crecimiento continuo!`,
      generationTool: GENERATION_TOOL.linkedinPost,
    },
    [TOV_TEST_OPTION.emailFeedbackSurvey]: {
      title: 'Email - Encuesta de satisfacción',
      text: `Estimado/a [Nombre del cliente], En ACME Corp, nos esforzamos por ofrecer experiencias excepcionales a clientes como usted. Agradeceríamos mucho sus comentarios a través de una breve encuesta de 5 minutos. Sus respuestas sinceras nos ayudarán a mejorar nuestros productos/servicios. La encuesta es anónima, y su participación marcará una gran diferencia. Haga clic aquí para responder a la encuesta: [Enlace de la encuesta] ¡Gracias por su tiempo y apoyo! Atentamente, El equipo de ACME Corp`,
      generationTool: GENERATION_TOOL.emailBody,
    },
    [TOV_TEST_OPTION.emailFeatureAnnouncement]: {
      title: 'Email - Anuncio de nueva función',
      text: `Estimado/a [Nombre del cliente], ¡Estamos emocionados de presentar una nueva función que mejorará su experiencia con ACME Corp! Nuestra última actualización incluye análisis avanzados de datos para obtener insights más profundos y tomar decisiones informadas. Disfrute de análisis en tiempo real y reportes personalizables adaptados a sus necesidades. Creemos que esta innovación aumentará su productividad y optimizará sus flujos de trabajo. Inicie sesión para explorar esta nueva función hoy mismo. ¡Gracias por su continuo apoyo! Atentamente, El equipo de ACME Corp`,
      generationTool: GENERATION_TOOL.emailBody,
    },
  },
  [LANGUAGE.tr]: {
    [TOV_TEST_OPTION.fbPostWebinar]: {
      title: 'Facebook Gönderisi - Webinar Daveti',
      text: 'ACME Corp’un özel webinarına katılın! Sektördeki en son trendleri, uzman görüşlerini ve işinizi ileriye taşıyacak uygulanabilir stratejileri keşfedin. Alanında lider isimlerden öğrenin ve benzer düşünen profesyonellerle iletişime geçin. Uzmanlarımız, en son gelişmeleri ve başarı için nasıl uygulanacaklarını paylaşacak. Yerinizi ayırtmak ve değerli bilgiler almak için hemen kaydolun. Ayrıntılar için web sitemizi ziyaret edin. Sizi orada görmeyi sabırsızlıkla bekliyoruz!',
      generationTool: GENERATION_TOOL.facebookPost,
    },
    [TOV_TEST_OPTION.liPostRenewable]: {
      title: 'LinkedIn Gönderisi - Yenilenebilir Enerji',
      text: 'Heyecan verici haberler! ACME Corp’un yeni topluluk güneş enerjisi projeleri ve yenilenebilir enerji anlaşmaları 11 eyaleti dönüştürüyor. Bu projeler, yerel şebekelere temiz enerji ekleyecek, iş yaratacak, kırsal bölgeleri destekleyecek ve ACME Corp’un yenilenebilir enerji hedeflerini ileriye taşıyacak. Portföyümüz, 10 ülkede 600’den fazla yenilenebilir enerji projesini içeriyor. Bu girişimlerin müşterilere nasıl tasarruf sağlayacağını ve Amerika’nın enerji şebekesini nasıl güçlendireceğini öğrenin. Daha fazla bilgi edinin: [Link ekleyin]',
      generationTool: GENERATION_TOOL.linkedinPost,
    },
    [TOV_TEST_OPTION.liPostWorkplace]: {
      title: 'LinkedIn Gönderisi - En İyi Çalışma Ortamı',
      text: 'ACME Corp’un Glassdoor’un 2024’te Çalışmak İçin En İyi Yerler listesinde yer aldığını duyurmaktan mutluluk duyuyoruz! Bu onur, ekibimizin bağlılığını ve inşa ettiğimiz kapsayıcı, destekleyici çalışma ortamını yansıtıyor. Organizasyonumuzun her bir üyesine katkılarından dolayı minnettarız. Glassdoor’a ve harika ekibimize teşekkür ederiz. Başarı ve büyümenin devamını dileriz!',
      generationTool: GENERATION_TOOL.linkedinPost,
    },
    [TOV_TEST_OPTION.emailFeedbackSurvey]: {
      title: 'Geri Bildirim Anketi E-postası',
      text: 'Sayın [Müşteri Adı], ACME Corp olarak sizin gibi müşterilere olağanüstü deneyimler sunmak için çabalıyoruz. Kısa bir 5 dakikalık anketle geri bildiriminizi almayı çok isteriz. Dürüst yanıtlarınız, ürünlerimizi/hizmetlerimizi geliştirmemize yardımcı olacaktır. Anket anonimdir ve katılımınız gerçekten fark yaratacaktır. Ankete katılmak için buraya tıklayın: [Anket Linki] Zamanınız ve desteğiniz için teşekkür ederiz! Saygılarımızla, ACME Corp Ekibi',
      generationTool: GENERATION_TOOL.emailBody,
    },
    [TOV_TEST_OPTION.emailFeatureAnnouncement]: {
      title: 'Özellik Duyuru E-postası',
      text: 'Sayın [Müşteri Adı], ACME Corp deneyiminizi geliştirecek yeni bir özelliği tanıtmaktan heyecan duyuyoruz! Son güncellememiz, daha derinlemesine içgörüler ve bilinçli kararlar için gelişmiş veri analitiği içeriyor. İhtiyaçlarınıza göre özelleştirilmiş raporlar ve gerçek zamanlı analizlerden yararlanın. Bu yeniliğin verimliliğinizi artıracağını ve iş akışlarını kolaylaştıracağını düşünüyoruz. Bu yeni özelliği keşfetmek için hemen giriş yapın! Sürekli desteğiniz için teşekkür ederiz. Saygılarımızla, ACME Corp Ekibi',
      generationTool: GENERATION_TOOL.emailBody,
    },
  },
  [LANGUAGE.fr]: {
    [TOV_TEST_OPTION.fbPostWebinar]: {
      title: 'Publication Facebook - Invitation à un Webinar',
      text: 'Rejoignez le webinar exclusif d’ACME Corp ! Découvrez les dernières tendances de l’industrie, les perspectives d’experts et les stratégies concrètes pour développer votre entreprise. Apprenez des leaders du secteur et rencontrez des professionnels partageant les mêmes idées. Nos experts partageront leurs connaissances sur les dernières avancées et comment les appliquer avec succès. Inscrivez-vous dès maintenant pour réserver votre place et obtenir des informations précieuses. Visitez notre site web pour plus de détails. Nous avons hâte de vous y voir !',
      generationTool: GENERATION_TOOL.facebookPost,
    },
    [TOV_TEST_OPTION.liPostRenewable]: {
      title: 'Publication LinkedIn - Énergie Renouvelable',
      text: 'Bonne nouvelle ! Les nouveaux projets solaires communautaires et les accords sur les énergies renouvelables d’ACME Corp transforment 11 états. Ces projets vont ajouter de l’énergie propre aux réseaux locaux, créer des emplois, soutenir les zones rurales et faire progresser les objectifs d’énergies renouvelables d’ACME Corp. Notre portefeuille comprend plus de 600 projets d’énergies renouvelables dans 10 pays. Découvrez comment ces initiatives permettront de réaliser des économies pour les clients et de renforcer le réseau énergétique américain. En savoir plus : [Insérer le lien]',
      generationTool: GENERATION_TOOL.linkedinPost,
    },
    [TOV_TEST_OPTION.liPostWorkplace]: {
      title: 'Publication LinkedIn - Meilleur Lieu de Travail',
      text: 'Nous sommes ravis d’annoncer qu’ACME Corp a été nommé parmi les Meilleurs Lieux de Travail sur Glassdoor pour 2024 ! Cet honneur reflète le dévouement de notre équipe et l’environnement de travail inclusif et solidaire que nous avons construit. Nous remercions chaque membre de notre organisation pour leurs contributions. Merci à Glassdoor et à notre équipe incroyable. Que le succès et la croissance continuent !',
      generationTool: GENERATION_TOOL.linkedinPost,
    },
    [TOV_TEST_OPTION.emailFeedbackSurvey]: {
      title: 'Email de Sondage de Satisfaction',
      text: 'Cher/chère [Nom du client], Chez ACME Corp, nous nous efforçons d’offrir des expériences exceptionnelles à des clients comme vous. Nous serions ravis d’avoir votre avis via un court sondage de 5 minutes. Vos réponses honnêtes nous aideront à améliorer nos produits/services. Le sondage est anonyme et votre participation fera vraiment la différence. Cliquez ici pour répondre au sondage : [Lien du sondage] Merci pour votre temps et votre soutien ! Cordialement, L’équipe d’ACME Corp',
      generationTool: GENERATION_TOOL.emailBody,
    },
    [TOV_TEST_OPTION.emailFeatureAnnouncement]: {
      title: 'E-mail d’Annonce de Nouvelle Fonction',
      text: 'Cher/chère [Nom du client], Nous sommes ravis de vous présenter une nouvelle fonctionnalité qui améliorera votre expérience ACME Corp ! Notre dernière mise à jour comprend des analyses de données avancées pour des informations plus approfondies et des décisions éclairées. Profitez d’analyses en temps réel et de rapports personnalisables adaptés à vos besoins. Nous pensons que cette innovation augmentera votre productivité et simplifiera vos flux de travail. Connectez-vous dès aujourd’hui pour découvrir cette nouvelle fonctionnalité ! Merci pour votre soutien continu. Cordialement, L’équipe d’ACME Corp',
      generationTool: GENERATION_TOOL.emailBody,
    },
  },
  [LANGUAGE.fr_qu]: {
    [TOV_TEST_OPTION.fbPostWebinar]: {
      title: 'Publication Facebook - Invitation à un webinaire',
      text: `Participez au webinaire exclusif d'ACME Corp! Découvrez les dernières tendances du secteur, des perspectives d'experts et des stratégies concrètes pour faire avancer votre entreprise. Apprenez des leaders du domaine et échangez avec des professionnels partageant vos intérêts. Nos experts partageront leurs connaissances sur les dernières avancées et comment les appliquer pour réussir. Inscrivez-vous dès maintenant pour réserver votre place et profiter de précieuses informations. Visitez notre site web pour plus de détails. Nous avons hâte de vous y voir!`,
      generationTool: GENERATION_TOOL.facebookPost,
    },
    [TOV_TEST_OPTION.liPostRenewable]: {
      title: 'Publication LinkedIn - Énergie renouvelable',
      text: `Grande nouvelle! Les nouveaux projets solaires communautaires et accords d’énergie renouvelable d’ACME Corp transforment 11 états. Ces projets ajouteront de l’énergie propre aux réseaux locaux, créeront des emplois, soutiendront les zones rurales et feront progresser les objectifs d’ACME Corp en matière d’énergies renouvelables. Notre portefeuille comprend plus de 600 projets d’énergie renouvelable dans 10 pays. Découvrez comment ces initiatives offriront des économies aux clients et amélioreront le réseau énergétique américain. Plus d’infos ici : [Insérez le lien]`,
      generationTool: GENERATION_TOOL.linkedinPost,
    },
    [TOV_TEST_OPTION.liPostWorkplace]: {
      title: 'Publication LinkedIn - Meilleur employeur',
      text: `Nous sommes ravis d’annoncer qu’ACME Corp a été nommé parmi les Meilleurs lieux de travail 2024 par Glassdoor! Cet honneur reflète le dévouement de notre équipe et l’environnement de travail inclusif et solidaire que nous avons créé. Nous sommes reconnaissants envers chaque membre de notre organisation pour leurs contributions. Merci à Glassdoor et à notre équipe exceptionnelle. À un succès et une croissance continus!`,
      generationTool: GENERATION_TOOL.linkedinPost,
    },
    [TOV_TEST_OPTION.emailFeedbackSurvey]: {
      title: 'Courriel - Enquête de satisfaction',
      text: `Cher/Chère [Nom du client], Chez ACME Corp, nous nous efforçons de fournir des expériences exceptionnelles à des clients comme vous. Nous apprécierions grandement votre feedback via un court sondage de 5 minutes. Vos réponses honnêtes nous aideront à améliorer nos produits/services. Le sondage est anonyme, et votre participation fera une réelle différence. Cliquez ici pour répondre au sondage : [Lien du sondage] Merci pour votre temps et votre soutien! Cordialement, L’équipe ACME Corp`,
      generationTool: GENERATION_TOOL.emailBody,
    },
    [TOV_TEST_OPTION.emailFeatureAnnouncement]: {
      title: 'Courriel - Annonce de fonctionnalité',
      text: `Cher/Chère [Nom du client], Nous sommes ravis de vous présenter une nouvelle fonctionnalité qui améliore votre expérience avec ACME Corp! Notre dernière mise à jour inclut des analyses de données avancées pour des insights approfondis et des décisions éclairées. Profitez d’une analyse en temps réel et de rapports personnalisables adaptés à vos besoins. Nous croyons que cette innovation augmentera votre productivité et simplifiera vos flux de travail. Connectez-vous pour explorer cette nouvelle fonctionnalité dès aujourd’hui! Merci pour votre soutien continu. Cordialement, L’équipe ACME Corp`,
      generationTool: GENERATION_TOOL.emailBody,
    },
  },
  [LANGUAGE.bg]: {
    [TOV_TEST_OPTION.fbPostWebinar]: {
      title: 'Публикация във Facebook – Покана за уебинар',
      text: 'Присъединете се към ексклузивния уебинар на ACME Corp! Открийте най-новите тенденции в индустрията, експертни прозрения и практически стратегии, за да развиете бизнеса си. Учете се от лидерите в областта и се свържете с професионалисти със сходни интереси. Нашите експерти ще споделят знания за последните иновации и как да ги приложите успешно. Регистрирайте се сега, за да запазите своето място и да получите ценни знания. Посетете нашия сайт за подробности. Очакваме с нетърпение да ви видим!',
      generationTool: GENERATION_TOOL.facebookPost,
    },
    [TOV_TEST_OPTION.liPostRenewable]: {
      title: 'Публикация в LinkedIn – Възобновяема енергия',
      text: 'Вълнуващи новини! Новите соларни проекти и споразумения за възобновяема енергия на ACME Corp променят 11 щата. Тези проекти ще добавят чиста енергия към местните мрежи, ще създадат работни места, ще подкрепят селските райони и ще допринесат за постигането на целите на ACME Corp за възобновяеми източници. Нашето портфолио включва над 600 проекта за възобновяема енергия в 10 държави. Научете повече за това как тези инициативи ще донесат икономии за клиентите и ще подобрят енергийната мрежа в Америка. Научете повече: [Въведете връзка]',
      generationTool: GENERATION_TOOL.linkedinPost,
    },
    [TOV_TEST_OPTION.liPostWorkplace]: {
      title: 'Публикация в LinkedIn – Най-добро място за работа',
      text: 'С вълнение съобщаваме, че ACME Corp е обявена за едно от най-добрите места за работа в класацията на Glassdoor за 2024 г.! Това признание отразява отдадеността на нашия екип и подкрепящата и приобщаваща работна среда, която сме създали. Благодарим на всеки член на нашата организация за неговия принос. Благодарим на Glassdoor и на нашия невероятен екип. Пожелаваме още успехи и растеж!',
      generationTool: GENERATION_TOOL.linkedinPost,
    },
    [TOV_TEST_OPTION.emailFeedbackSurvey]: {
      title: 'Имейл с анкета за обратна връзка',
      text: 'Уважаеми/а [Име на клиента], В ACME Corp се стремим да предоставяме изключителни изживявания на клиенти като вас. Ще се радваме, ако отделите време за попълване на кратка, 5-минутна анкета. Вашите честни отговори ще ни помогнат да подобрим нашите продукти/услуги. Анкетата е анонимна и вашето участие наистина ще има значение. Натиснете тук, за да попълните анкетата: [Линк към анкетата] Благодарим ви за отделеното време и подкрепа! Искрено, Екипът на ACME Corp',
      generationTool: GENERATION_TOOL.emailBody,
    },
    [TOV_TEST_OPTION.emailFeatureAnnouncement]: {
      title: 'Имейл за обявяване на нова функция',
      text: 'Уважаеми/а [Име на клиента], С вълнение ви представяме нова функция, която ще подобри вашето изживяване с ACME Corp! Нашата последна актуализация включва разширени данни за анализ за по-дълбоки прозрения и информирани решения. Насладете се на анализи в реално време и персонализирани отчети, съобразени с вашите нужди. Вярваме, че тази иновация ще повиши вашата производителност и ще оптимизира работните процеси. Влезте, за да разгледате тази нова функция днес! Благодарим ви за вашата постоянна подкрепа. С уважение, Екипът на ACME Corp',
      generationTool: GENERATION_TOOL.emailBody,
    },
  },
  [LANGUAGE.cs]: {
    [TOV_TEST_OPTION.fbPostWebinar]: {
      title: 'Facebook Příspěvek – Pozvánka na webinář',
      text: 'Připojte se k exkluzivnímu webináři od ACME Corp! Objevte nejnovější trendy v oboru, odborné pohledy a praktické strategie, jak pozvednout své podnikání. Učte se od předních odborníků a navazujte kontakty s podobně smýšlejícími profesionály. Naši experti se podělí o nejnovější poznatky a jak je úspěšně aplikovat. Zaregistrujte se nyní, abyste si zajistili místo a získali cenné poznatky. Navštivte náš web pro podrobnosti. Těšíme se na vás!',
      generationTool: GENERATION_TOOL.facebookPost,
    },
    [TOV_TEST_OPTION.liPostRenewable]: {
      title: 'LinkedIn Příspěvek – Obnovitelná energie',
      text: 'Skvělé novinky! Nové komunitní solární projekty a dohody o obnovitelných energiích od ACME Corp mění situaci v 11 státech. Tyto projekty přinesou čistou energii do místních sítí, vytvoří pracovní místa, podpoří venkovské oblasti a podpoří cíle ACME Corp v oblasti obnovitelných zdrojů. Náš portfolio zahrnuje více než 600 projektů obnovitelné energie v 10 zemích. Zjistěte, jak tyto iniciativy přinesou úspory zákazníkům a zlepší energetickou síť USA. Více informací: [Vložte odkaz]',
      generationTool: GENERATION_TOOL.linkedinPost,
    },
    [TOV_TEST_OPTION.liPostWorkplace]: {
      title: 'LinkedIn Příspěvek – Nejlepší pracovní místo',
      text: 'S nadšením oznamujeme, že ACME Corp bylo jmenováno jedním z nejlepších pracovišť na Glassdoor pro rok 2024! Toto ocenění odráží nasazení našeho týmu a inkluzivní, podporující pracovní prostředí, které jsme vybudovali. Jsme vděční každému členovi naší organizace za jejich přínos. Děkujeme Glassdoor a našemu úžasnému týmu. Ať pokračují úspěchy a růst!',
      generationTool: GENERATION_TOOL.linkedinPost,
    },
    [TOV_TEST_OPTION.emailFeedbackSurvey]: {
      title: 'E-mail s průzkumem spokojenosti',
      text: 'Vážený/á [Jméno zákazníka], v ACME Corp se snažíme poskytovat výjimečné zkušenosti pro zákazníky, jako jste vy. Byli bychom vám velmi vděční za vaši zpětnou vazbu prostřednictvím krátkého, 5minutového průzkumu. Vaše upřímné odpovědi nám pomohou zlepšit naše produkty/služby. Průzkum je anonymní a vaše účast bude mít skutečný dopad. Klikněte zde pro účast na průzkumu: [Odkaz na průzkum] Děkujeme za váš čas a podporu! S úctou, Tým ACME Corp',
      generationTool: GENERATION_TOOL.emailBody,
    },
    [TOV_TEST_OPTION.emailFeatureAnnouncement]: {
      title: 'E-mail s Oznámením Funkce',
      text: 'Vážený/á [Jméno zákazníka], s potěšením vám představujeme novou funkci, která vylepší vaše zkušenosti s ACME Corp! Naše poslední aktualizace zahrnuje pokročilé analýzy dat pro hlubší poznatky a informovaná rozhodnutí. Využijte analýzy v reálném čase a přizpůsobitelné zprávy dle vašich potřeb. Věříme, že tato inovace zvýší vaši produktivitu a zjednoduší pracovní postupy. Přihlaste se ještě dnes a prozkoumejte tuto novou funkci! Děkujeme za vaši neustálou podporu. S pozdravem, Tým ACME Corp',
      generationTool: GENERATION_TOOL.emailBody,
    },
  },
  [LANGUAGE.da]: {
    [TOV_TEST_OPTION.fbPostWebinar]: {
      title: 'Facebook Oplæg – Webinar Invitation',
      text: 'Deltag i ACME Corps eksklusive webinar! Opdag de nyeste branchetendenser, ekspertindsigt og handlingsorienterede strategier for at løfte din forretning. Lær af brancheledere og netværk med ligesindede fagfolk. Vores eksperter vil dele deres viden om de nyeste fremskridt og hvordan man kan anvende dem med succes. Tilmeld dig nu for at sikre din plads og få værdifulde indsigter. Besøg vores hjemmeside for detaljer. Vi ser frem til at se dig!',
      generationTool: GENERATION_TOOL.facebookPost,
    },
    [TOV_TEST_OPTION.liPostRenewable]: {
      title: 'LinkedIn Oplæg – Vedvarende Energi',
      text: 'Spændende nyheder! ACME Corps nye solenergi-projekter og aftaler om vedvarende energi omdanner 11 stater. Disse projekter vil tilføje ren energi til lokale netværk, skabe arbejdspladser, støtte landdistrikter og fremme ACME Corps mål for vedvarende energi. Vores portefølje omfatter over 600 vedvarende energi-projekter på tværs af 10 lande. Læs hvordan disse initiativer vil give besparelser til kunderne og forbedre USA’s energinet. Læs mere: [Indsæt link]',
      generationTool: GENERATION_TOOL.linkedinPost,
    },
    [TOV_TEST_OPTION.liPostWorkplace]: {
      title: 'LinkedIn Oplæg – Bedste Arbejdsplads',
      text: 'Vi er glade for at kunne meddele, at ACME Corp er blevet kåret som en af de bedste arbejdspladser på Glassdoor for 2024! Denne ære afspejler vores teams dedikation og det inkluderende, støttende arbejdsmiljø, vi har skabt. Vi er taknemmelige for hvert medlem af vores organisation for deres bidrag. Tak til Glassdoor og vores fantastiske team. Her er til fortsat succes og vækst!',
      generationTool: GENERATION_TOOL.linkedinPost,
    },
    [TOV_TEST_OPTION.emailFeedbackSurvey]: {
      title: 'E-mail med Tilfredshedsundersøgelse',
      text: 'Kære [Kundens navn], Hos ACME Corp stræber vi efter at levere enestående oplevelser til kunder som dig. Vi vil sætte stor pris på din feedback gennem en kort, 5-minutters undersøgelse. Dine ærlige svar vil hjælpe os med at forbedre vores produkter/tjenester. Undersøgelsen er anonym, og din deltagelse vil virkelig gøre en forskel. Klik her for at deltage i undersøgelsen: [Undersøgelseslink] Tak for din tid og støtte! Venlig hilsen, ACME Corp-teamet',
      generationTool: GENERATION_TOOL.emailBody,
    },
    [TOV_TEST_OPTION.emailFeatureAnnouncement]: {
      title: 'E-mail med Funktionsopdatering',
      text: 'Kære [Kundens navn], Vi er glade for at kunne præsentere en ny funktion, der forbedrer din oplevelse med ACME Corp! Vores seneste opdatering inkluderer avanceret dataanalyse for dybere indsigt og informerede beslutninger. Nyd realtidsanalyser og tilpassede rapporter, der er skræddersyet til dine behov. Vi mener, at denne innovation vil øge din produktivitet og optimere arbejdsgange. Log ind i dag for at udforske denne nye funktion! Tak for din fortsatte støtte. Med venlig hilsen, ACME Corp-teamet',
      generationTool: GENERATION_TOOL.emailBody,
    },
  },
  [LANGUAGE.nl]: {
    [TOV_TEST_OPTION.fbPostWebinar]: {
      title: 'Facebook Post – Webinar Uitnodiging',
      text: 'Doe mee aan het exclusieve webinar van ACME Corp! Ontdek de nieuwste trends in de branche, deskundig advies en praktische strategieën om uw bedrijf te laten groeien. Leer van leiders in het veld en netwerk met gelijkgestemde professionals. Onze experts delen hun kennis over de nieuwste ontwikkelingen en hoe u deze succesvol kunt toepassen. Registreer nu om uw plek te reserveren en waardevolle inzichten te krijgen. Bezoek onze website voor meer details. We kijken ernaar uit om u daar te zien!',
      generationTool: GENERATION_TOOL.facebookPost,
    },
    [TOV_TEST_OPTION.liPostRenewable]: {
      title: 'LinkedIn Post – Hernieuwbare Energie',
      text: 'Spannend nieuws! De nieuwe zonne-energieprojecten en overeenkomsten over hernieuwbare energie van ACME Corp veranderen 11 staten. Deze projecten zullen schone energie toevoegen aan lokale netwerken, banen creëren, landelijke gebieden ondersteunen en bijdragen aan de hernieuwbare doelen van ACME Corp. Ons portfolio omvat meer dan 600 projecten op het gebied van hernieuwbare energie in 10 landen. Ontdek hoe deze initiatieven besparingen voor klanten zullen opleveren en het Amerikaanse energienet zullen versterken. Meer informatie: [Link invoegen]',
      generationTool: GENERATION_TOOL.linkedinPost,
    },
    [TOV_TEST_OPTION.liPostWorkplace]: {
      title: 'LinkedIn Post – Beste Werkplek',
      text: 'We zijn verheugd aan te kondigen dat ACME Corp is uitgeroepen tot een van de Beste Werkplekken op Glassdoor voor 2024! Deze erkenning weerspiegelt de toewijding van ons team en de inclusieve, ondersteunende werkomgeving die we hebben gecreëerd. We zijn dankbaar voor elk lid van onze organisatie voor hun bijdragen. Bedankt aan Glassdoor en ons geweldige team. Op naar nog meer succes en groei!',
      generationTool: GENERATION_TOOL.linkedinPost,
    },
    [TOV_TEST_OPTION.emailFeedbackSurvey]: {
      title: 'E-mail voor Klanttevredenheidsonderzoek',
      text: 'Beste [Naam klant], Bij ACME Corp streven we ernaar om uitzonderlijke ervaringen te bieden aan klanten zoals u. We zouden het zeer op prijs stellen als u een korte enquête van 5 minuten zou invullen. Uw eerlijke antwoorden helpen ons om onze producten/diensten te verbeteren. De enquête is anoniem en uw deelname zal echt een verschil maken. Klik hier om de enquête in te vullen: [Enquête link] Bedankt voor uw tijd en steun! Met vriendelijke groet, Het ACME Corp-team',
      generationTool: GENERATION_TOOL.emailBody,
    },
    [TOV_TEST_OPTION.emailFeatureAnnouncement]: {
      title: 'E-mail met Aankondiging van Nieuwe Functie',
      text: 'Beste [Naam klant], We zijn verheugd om een nieuwe functie te introduceren die uw ervaring met ACME Corp zal verbeteren! Onze nieuwste update omvat geavanceerde data-analyse voor diepere inzichten en weloverwogen beslissingen. Geniet van realtime analyses en aanpasbare rapportages op maat van uw behoeften. We geloven dat deze innovatie uw productiviteit zal verhogen en uw werkstromen zal optimaliseren. Log vandaag nog in om deze nieuwe functie te verkennen! Bedankt voor uw voortdurende steun. Met vriendelijke groet, Het ACME Corp-team',
      generationTool: GENERATION_TOOL.emailBody,
    },
  },
  [LANGUAGE.hu]: {
    [TOV_TEST_OPTION.fbPostWebinar]: {
      title: 'Facebook Bejegyzés – Webinar Meghívó',
      text: 'Csatlakozzon az ACME Corp exkluzív webináriumához! Fedezze fel az iparág legújabb trendjeit, szakértői betekintéseket és a vállalkozása fellendítéséhez szükséges gyakorlati stratégiákat. Tanuljon az iparág vezetőitől, és lépjen kapcsolatba hasonló gondolkodású szakemberekkel. Szakértőink megosztják tudásukat a legújabb fejleményekről, és arról, hogyan alkalmazhatja ezeket a siker érdekében. Regisztráljon most, hogy biztosítsa a helyét, és értékes betekintést nyerjen. További részletekért látogasson el weboldalunkra. Alig várjuk, hogy találkozzunk Önnel!',
      generationTool: GENERATION_TOOL.facebookPost,
    },
    [TOV_TEST_OPTION.liPostRenewable]: {
      title: 'LinkedIn Bejegyzés – Megújuló Energia',
      text: 'Izgalmas hírek! Az ACME Corp új közösségi napenergia-projektjei és megújuló energiaforrásokra vonatkozó megállapodásai 11 államot alakítanak át. Ezek a projektek tiszta energiát adnak a helyi hálózatokhoz, munkahelyeket teremtenek, támogatják a vidéki területeket, és hozzájárulnak az ACME Corp megújuló energiaforrásokra kitűzött céljainak eléréséhez. Portfóliónk több mint 600 megújuló energia projektet foglal magába, 10 országban. Tudja meg, hogyan hoznak ezek a kezdeményezések megtakarítást az ügyfelek számára, és hogyan erősítik Amerika energiaellátását. Tudjon meg többet: [Helyezze be a linket]',
      generationTool: GENERATION_TOOL.linkedinPost,
    },
    [TOV_TEST_OPTION.liPostWorkplace]: {
      title: 'LinkedIn Bejegyzés – Legjobb Munkahely',
      text: 'Örömmel jelentjük be, hogy az ACME Corp bekerült a Glassdoor legjobb munkahelyei közé 2024-re! Ez az elismerés csapatunk elkötelezettségét és az általunk kialakított befogadó, támogató munkakörnyezetet tükrözi. Hálásak vagyunk szervezetünk minden egyes tagjának a hozzájárulásáért. Köszönjük a Glassdoornak és csodálatos csapatunknak. További sikerekben és növekedésben gazdag jövőt kívánunk!',
      generationTool: GENERATION_TOOL.linkedinPost,
    },
    [TOV_TEST_OPTION.emailFeedbackSurvey]: {
      title: 'Visszajelzési Kérdőív E-mail',
      text: 'Kedves [Vevő neve], az ACME Corp-nál arra törekszünk, hogy kivételes élményeket nyújtsunk az önhöz hasonló ügyfelek számára. Nagyra értékelnénk visszajelzését egy rövid, 5 perces kérdőív kitöltésével. Őszinte válaszai segítenek nekünk termékeink/szolgáltatásaink fejlesztésében. A kérdőív anonim, és részvétele valódi változást hozhat. Kérjük, kattintson ide a kérdőív kitöltéséhez: [Kérdőív Link] Köszönjük, hogy rászánta idejét és támogatását! Üdvözlettel, Az ACME Corp Csapata',
      generationTool: GENERATION_TOOL.emailBody,
    },
    [TOV_TEST_OPTION.emailFeatureAnnouncement]: {
      title: 'Funkcióbejelentő E-mail',
      text: 'Kedves [Vevő neve], örömmel mutatunk be egy új funkciót, amely tovább javítja az ACME Corp-élményét! Legújabb frissítésünk fejlett adatelemzést tartalmaz, mélyebb betekintést nyújtva és segítve a megalapozott döntéshozatalt. Élvezze a valós idejű elemzést és az igényeire szabott testreszabható jelentéseket. Úgy gondoljuk, hogy ez az innováció növeli a termelékenységet és egyszerűsíti a munkafolyamatokat. Jelentkezzen be most, hogy felfedezze ezt az új funkciót! Köszönjük folyamatos támogatását. Üdvözlettel, Az ACME Corp Csapata',
      generationTool: GENERATION_TOOL.emailBody,
    },
  },
  [LANGUAGE.it]: {
    [TOV_TEST_OPTION.fbPostWebinar]: {
      title: 'Post su Facebook – Invito al Webinar',
      text: "Partecipa al webinar esclusivo di ACME Corp! Scopri le ultime tendenze del settore, le intuizioni degli esperti e le strategie pratiche per migliorare la tua attività. Impara dai leader del settore e fai rete con professionisti che la pensano come te. I nostri esperti condivideranno conoscenze sugli ultimi sviluppi e su come applicarli con successo. Iscriviti ora per assicurarti il tuo posto e ottenere preziose informazioni. Visita il nostro sito web per ulteriori dettagli. Non vediamo l'ora di vederti!",
      generationTool: GENERATION_TOOL.facebookPost,
    },
    [TOV_TEST_OPTION.liPostRenewable]: {
      title: 'Post su LinkedIn – Energia Rinnovabile',
      text: 'Notizie entusiasmanti! I nuovi progetti di energia solare comunitaria e gli accordi di energia rinnovabile di ACME Corp stanno trasformando 11 stati. Questi progetti aggiungeranno energia pulita alle reti locali, creeranno posti di lavoro, sosterranno le aree rurali e promuoveranno gli obiettivi di ACME Corp in materia di energia rinnovabile. Il nostro portafoglio comprende oltre 600 progetti di energia rinnovabile in 10 paesi. Scopri come queste iniziative porteranno risparmi ai clienti e miglioreranno la rete energetica americana. Scopri di più: [Inserisci link]',
      generationTool: GENERATION_TOOL.linkedinPost,
    },
    [TOV_TEST_OPTION.liPostWorkplace]: {
      title: 'Post su LinkedIn – Miglior Posto di Lavoro',
      text: "Siamo entusiasti di annunciare che ACME Corp è stata nominata uno dei Migliori Posti di Lavoro su Glassdoor per il 2024! Questo onore riflette la dedizione del nostro team e l'ambiente di lavoro inclusivo e di supporto che abbiamo costruito. Siamo grati a ogni membro della nostra organizzazione per i loro contributi. Grazie a Glassdoor e al nostro incredibile team. Ecco a un successo e una crescita continui!",
      generationTool: GENERATION_TOOL.linkedinPost,
    },
    [TOV_TEST_OPTION.emailFeedbackSurvey]: {
      title: 'E-mail di Sondaggio di Feedback',
      text: 'Gentile [Nome del Cliente], in ACME Corp ci impegniamo a offrire esperienze eccezionali a clienti come te. Apprezzeremmo molto il tuo feedback tramite un breve sondaggio di 5 minuti. Le tue risposte oneste ci aiuteranno a migliorare i nostri prodotti/servizi. Il sondaggio è anonimo e la tua partecipazione farà davvero la differenza. Clicca qui per partecipare al sondaggio: [Link del sondaggio] Grazie per il tuo tempo e supporto! Cordialmente, Il team di ACME Corp',
      generationTool: GENERATION_TOOL.emailBody,
    },
    [TOV_TEST_OPTION.emailFeatureAnnouncement]: {
      title: 'E-mail di Annuncio Funzionalità',
      text: "Gentile [Nome del Cliente], siamo entusiasti di presentare una nuova funzionalità che migliorerà la tua esperienza con ACME Corp! Il nostro ultimo aggiornamento include analisi dei dati avanzate per approfondimenti più dettagliati e decisioni informate. Goditi l'analisi in tempo reale e report personalizzabili su misura per le tue esigenze. Crediamo che questa innovazione aumenterà la tua produttività e semplificherà i flussi di lavoro. Accedi oggi stesso per esplorare questa nuova funzionalità! Grazie per il tuo continuo supporto. Cordialmente, Il team di ACME Corp",
      generationTool: GENERATION_TOOL.emailBody,
    },
  },
  [LANGUAGE.pl]: {
    [TOV_TEST_OPTION.fbPostWebinar]: {
      title: 'Post na Facebooku – Zaproszenie na webinar',
      text: 'Dołącz do ekskluzywnego webinaru ACME Corp! Odkryj najnowsze trendy w branży, opinie ekspertów i praktyczne strategie, które pomogą rozwinąć Twój biznes. Ucz się od liderów branży i nawiązuj kontakty z profesjonalistami o podobnych poglądach. Nasi eksperci podzielą się wiedzą na temat najnowszych osiągnięć i jak je skutecznie zastosować. Zarejestruj się już teraz, aby zapewnić sobie miejsce i uzyskać cenne informacje. Odwiedź naszą stronę internetową, aby dowiedzieć się więcej. Czekamy na Ciebie!',
      generationTool: GENERATION_TOOL.facebookPost,
    },
    [TOV_TEST_OPTION.liPostRenewable]: {
      title: 'Post na LinkedIn – Energia odnawialna',
      text: 'Ekscytujące wieści! Nowe projekty społecznościowych systemów solarnych oraz umowy na energię odnawialną od ACME Corp zmieniają 11 stanów. Te projekty wprowadzą czystą energię do lokalnych sieci, stworzą miejsca pracy, wesprą obszary wiejskie i przyczynią się do realizacji celów ACME Corp w zakresie energii odnawialnej. Nasze portfolio obejmuje ponad 600 projektów odnawialnej energii w 10 krajach. Dowiedz się, jak te inicjatywy przyniosą oszczędności klientom i wzmocnią amerykańską sieć energetyczną. Dowiedz się więcej: [Wstaw link]',
      generationTool: GENERATION_TOOL.linkedinPost,
    },
    [TOV_TEST_OPTION.liPostWorkplace]: {
      title: 'Post na LinkedIn – Najlepsze miejsce pracy',
      text: 'Z radością informujemy, że ACME Corp zostało uznane za jedno z Najlepszych Miejsc Pracy według Glassdoor na rok 2024! To wyróżnienie odzwierciedla zaangażowanie naszego zespołu oraz wspierające i inkluzywne środowisko pracy, które stworzyliśmy. Jesteśmy wdzięczni każdemu członkowi naszej organizacji za ich wkład. Dziękujemy Glassdoor i naszemu niesamowitemu zespołowi. Oby kolejne lata były pełne sukcesów i rozwoju!',
      generationTool: GENERATION_TOOL.linkedinPost,
    },
    [TOV_TEST_OPTION.emailFeedbackSurvey]: {
      title: 'E-mail z ankietą opinii',
      text: 'Szanowny/a [Imię Klienta], w ACME Corp dążymy do tego, aby zapewnić wyjątkowe doświadczenia klientom takim jak Ty. Będziemy wdzięczni za Twoją opinię poprzez krótką, 5-minutową ankietę. Twoje szczere odpowiedzi pomogą nam udoskonalić nasze produkty/usługi. Ankieta jest anonimowa, a Twój udział naprawdę się liczy. Kliknij tutaj, aby wypełnić ankietę: [Link do Ankiety] Dziękujemy za Twój czas i wsparcie! Z poważaniem, Zespół ACME Corp',
      generationTool: GENERATION_TOOL.emailBody,
    },
    [TOV_TEST_OPTION.emailFeatureAnnouncement]: {
      title: 'E-mail z Ogłoszeniem Nowej Funkcji',
      text: 'Szanowny/a [Imię Klienta], z radością przedstawiamy nową funkcję, która poprawi Twoje doświadczenie z ACME Corp! Nasza najnowsza aktualizacja obejmuje zaawansowaną analitykę danych, zapewniając głębszy wgląd i umożliwiając podejmowanie lepiej poinformowanych decyzji. Korzystaj z analizy w czasie rzeczywistym oraz dostosowywanych raportów dopasowanych do Twoich potrzeb. Wierzymy, że ta innowacja zwiększy Twoją produktywność i usprawni procesy pracy. Zaloguj się już teraz, aby odkryć tę nową funkcję! Dziękujemy za Twoje stałe wsparcie. Z poważaniem, Zespół ACME Corp',
      generationTool: GENERATION_TOOL.emailBody,
    },
  },
  [LANGUAGE.pt_br]: {
    [TOV_TEST_OPTION.fbPostWebinar]: {
      title: 'Post no Facebook – Convite para Webinar',
      text: 'Participe do webinar exclusivo da ACME Corp! Descubra as últimas tendências do setor, percepções de especialistas e estratégias práticas para impulsionar o seu negócio. Aprenda com líderes do setor e conecte-se com profissionais que pensam como você. Nossos especialistas compartilharão conhecimentos sobre os desenvolvimentos mais recentes e como aplicá-los para alcançar o sucesso. Inscreva-se agora para garantir a sua vaga e obter informações valiosas. Visite nosso site para mais detalhes. Esperamos vê-lo lá!',
      generationTool: GENERATION_TOOL.facebookPost,
    },
    [TOV_TEST_OPTION.liPostRenewable]: {
      title: 'Post no LinkedIn – Energia Renovável',
      text: 'Notícia empolgante! Os novos projetos solares comunitários e os acordos de energia renovável da ACME Corp estão transformando 11 estados. Esses projetos trarão energia limpa para as redes locais, criarão empregos, apoiarão áreas rurais e ajudarão a alcançar as metas de energia renovável da ACME Corp. Nosso portfólio inclui mais de 600 projetos de energia renovável em 10 países. Saiba como essas iniciativas proporcionarão economia para os clientes e melhorarão a rede energética dos EUA. Saiba mais: [Inserir link]',
      generationTool: GENERATION_TOOL.linkedinPost,
    },
    [TOV_TEST_OPTION.liPostWorkplace]: {
      title: 'Post no LinkedIn – Melhor Local de Trabalhar',
      text: 'Estamos muito contentes em anunciar que a ACME Corp foi nomeada como um dos Melhores Lugares para Trabalhar pelo Glassdoor em 2024! Essa honra reflete a dedicação de nossa equipe e o ambiente de trabalho inclusivo e de apoio que construímos. Agradecemos a cada membro de nossa organização por suas contribuições. Obrigado ao Glassdoor e à nossa incrível equipe. Que tenhamos ainda mais sucesso e crescimento no futuro!',
      generationTool: GENERATION_TOOL.linkedinPost,
    },
    [TOV_TEST_OPTION.emailFeedbackSurvey]: {
      title: 'E-mail de Pesquisa de Satisfação',
      text: 'Prezado/a [Nome do Cliente], Na ACME Corp, nos esforçamos para oferecer experiências excepcionais a clientes como você. Agradeceríamos muito se você pudesse responder a uma breve pesquisa de 5 minutos. Suas respostas sinceras nos ajudarão a melhorar nossos produtos/serviços. A pesquisa é anônima e sua participação realmente fará a diferença. Clique aqui para participar da pesquisa: [Link da pesquisa] Obrigado por seu tempo e apoio! Atenciosamente, Equipe ACME Corp',
      generationTool: GENERATION_TOOL.emailBody,
    },
    [TOV_TEST_OPTION.emailFeatureAnnouncement]: {
      title: 'E-mail de Anúncio de Nova Função',
      text: 'Prezado/a [Nome do Cliente], Estamos empolgados em apresentar um novo recurso que aprimora sua experiência com a ACME Corp! Nossa última atualização inclui análises avançadas de dados para obter insights mais profundos e decisões mais informadas. Aproveite a análise em tempo real e relatórios personalizáveis adaptados às suas necessidades. Acreditamos que essa inovação aumentará sua produtividade e otimizará fluxos de trabalho. Faça login para explorar este novo recurso hoje mesmo! Obrigado por seu contínuo apoio. Atenciosamente, Equipe ACME Corp',
      generationTool: GENERATION_TOOL.emailBody,
    },
  },
  [LANGUAGE.pt_pt]: {
    [TOV_TEST_OPTION.fbPostWebinar]: {
      title: 'Post no Facebook – Convite para Webinar',
      text: 'Participe do webinar exclusivo da ACME Corp! Descubra as últimas tendências do setor, percepções de especialistas e estratégias práticas para impulsionar o seu negócio. Aprenda com líderes do setor e conecte-se com profissionais que pensam como você. Nossos especialistas compartilharão conhecimentos sobre os desenvolvimentos mais recentes e como aplicá-los para alcançar o sucesso. Inscreva-se agora para garantir a sua vaga e obter informações valiosas. Visite nosso site para mais detalhes. Esperamos vê-lo lá!',
      generationTool: GENERATION_TOOL.facebookPost,
    },
    [TOV_TEST_OPTION.liPostRenewable]: {
      title: 'Post no LinkedIn – Energia Renovável',
      text: 'Notícia empolgante! Os novos projetos solares comunitários e os acordos de energia renovável da ACME Corp estão transformando 11 estados. Esses projetos trarão energia limpa para as redes locais, criarão empregos, apoiarão áreas rurais e ajudarão a alcançar as metas de energia renovável da ACME Corp. Nosso portfólio inclui mais de 600 projetos de energia renovável em 10 países. Saiba como essas iniciativas proporcionarão economia para os clientes e melhorarão a rede energética dos EUA. Saiba mais: [Inserir link]',
      generationTool: GENERATION_TOOL.linkedinPost,
    },
    [TOV_TEST_OPTION.liPostWorkplace]: {
      title: 'Post no LinkedIn – Melhor Local de Trabalhar',
      text: 'Estamos muito contentes em anunciar que a ACME Corp foi nomeada como um dos Melhores Lugares para Trabalhar pelo Glassdoor em 2024! Essa honra reflete a dedicação de nossa equipe e o ambiente de trabalho inclusivo e de apoio que construímos. Agradecemos a cada membro de nossa organização por suas contribuições. Obrigado ao Glassdoor e à nossa incrível equipe. Que tenhamos ainda mais sucesso e crescimento no futuro!',
      generationTool: GENERATION_TOOL.linkedinPost,
    },
    [TOV_TEST_OPTION.emailFeedbackSurvey]: {
      title: 'E-mail de Pesquisa de Satisfação',
      text: 'Prezado/a [Nome do Cliente], Na ACME Corp, nos esforçamos para oferecer experiências excepcionais a clientes como você. Agradeceríamos muito se você pudesse responder a uma breve pesquisa de 5 minutos. Suas respostas sinceras nos ajudarão a melhorar nossos produtos/serviços. A pesquisa é anônima e sua participação realmente fará a diferença. Clique aqui para participar da pesquisa: [Link da pesquisa] Obrigado por seu tempo e apoio! Atenciosamente, Equipe ACME Corp',
      generationTool: GENERATION_TOOL.emailBody,
    },
    [TOV_TEST_OPTION.emailFeatureAnnouncement]: {
      title: 'E-mail de Anúncio de Nova Função',
      text: 'Prezado/a [Nome do Cliente], Estamos empolgados em apresentar um novo recurso que aprimora sua experiência com a ACME Corp! Nossa última atualização inclui análises avançadas de dados para obter insights mais profundos e decisões mais informadas. Aproveite a análise em tempo real e relatórios personalizáveis adaptados às suas necessidades. Acreditamos que essa inovação aumentará sua produtividade e otimizará fluxos de trabalho. Faça login para explorar este novo recurso hoje mesmo! Obrigado por seu contínuo apoio. Atenciosamente, Equipe ACME Corp',
      generationTool: GENERATION_TOOL.emailBody,
    },
  },
  [LANGUAGE.ro]: {
    [TOV_TEST_OPTION.fbPostWebinar]: {
      title: 'Postare pe Facebook – Invitație la Webinar',
      text: 'Participă la webinarul exclusiv organizat de ACME Corp! Descoperă cele mai recente tendințe din industrie, perspectivele experților și strategiile practice pentru a-ți dezvolta afacerea. Învață de la liderii din domeniu și creează rețele cu profesioniști cu interese similare. Experții noștri vor împărtăși cunoștințe despre cele mai noi inovații și cum să le aplici pentru succes. Înscrie-te acum pentru a-ți rezerva locul și a obține informații valoroase. Vizitează site-ul nostru pentru mai multe detalii. Așteptăm cu nerăbdare să te vedem acolo!',
      generationTool: GENERATION_TOOL.facebookPost,
    },
    [TOV_TEST_OPTION.liPostRenewable]: {
      title: 'Postare pe LinkedIn – Energie Regenerabilă',
      text: 'Vești excelente! Noile proiecte solare comunitare și acordurile privind energia regenerabilă ale ACME Corp transformă 11 state. Aceste proiecte vor aduce energie curată rețelelor locale, vor crea locuri de muncă, vor sprijini zonele rurale și vor contribui la atingerea obiectivelor de energie regenerabilă ale ACME Corp. Portofoliul nostru include peste 600 de proiecte de energie regenerabilă în 10 țări. Află cum aceste inițiative vor aduce economii pentru clienți și vor îmbunătăți rețeaua energetică a Americii. Află mai multe: [Introduceți link]',
      generationTool: GENERATION_TOOL.linkedinPost,
    },
    [TOV_TEST_OPTION.liPostWorkplace]: {
      title: 'Postare pe LinkedIn – Cel Mai Bun Loc de Muncă',
      text: 'Suntem încântați să anunțăm că ACME Corp a fost numit unul dintre Cele Mai Bune Locuri de Muncă de pe Glassdoor pentru anul 2024! Această onoare reflectă devotamentul echipei noastre și mediul de lucru inclusiv și de susținere pe care l-am creat. Suntem recunoscători fiecărui membru al organizației noastre pentru contribuțiile lor. Mulțumim Glassdoor și echipei noastre incredibile. Să continuăm să avem succes și creștere!',
      generationTool: GENERATION_TOOL.linkedinPost,
    },
    [TOV_TEST_OPTION.emailFeedbackSurvey]: {
      title: 'E-mail de Chestionar pentru Feedback',
      text: 'Stimate [Nume Client], la ACME Corp ne străduim să oferim experiențe excepționale pentru clienți ca tine. Apreciem foarte mult feedback-ul tău printr-un scurt chestionar de 5 minute. Răspunsurile tale sincere ne vor ajuta să ne îmbunătățim produsele/serviciile. Chestionarul este anonim, iar participarea ta va face cu adevărat o diferență. Apasă aici pentru a completa chestionarul: [Link chestionar] Îți mulțumim pentru timpul și sprijinul tău! Cu stimă, Echipa ACME Corp',
      generationTool: GENERATION_TOOL.emailBody,
    },
    [TOV_TEST_OPTION.emailFeatureAnnouncement]: {
      title: 'E-mail de Anunțare a Noii Funcționalități',
      text: 'Stimate [Nume Client], Suntem încântați să prezentăm o nouă funcționalitate care îți îmbunătățește experiența cu ACME Corp! Ultima noastră actualizare include analize avansate de date pentru a oferi o perspectivă mai profundă și decizii mai informate. Bucură-te de analize în timp real și rapoarte personalizabile adaptate nevoilor tale. Credem că această inovație îți va crește productivitatea și va simplifica fluxurile de lucru. Conectează-te pentru a explora această nouă funcționalitate astăzi! Îți mulțumim pentru sprijinul tău continuu. Cu stimă, Echipa ACME Corp',
      generationTool: GENERATION_TOOL.emailBody,
    },
  },
  [LANGUAGE.ru]: {
    [TOV_TEST_OPTION.fbPostWebinar]: {
      title: 'Публикация в Facebook – Приглашение на вебинар',
      text: 'Присоединяйтесь к эксклюзивному вебинару компании ACME Corp! Узнайте о новейших тенденциях в отрасли, экспертных мнениях и практических стратегиях для развития вашего бизнеса. Учитесь у лидеров отрасли и налаживайте контакты с единомышленниками. Наши эксперты поделятся знаниями о последних достижениях и расскажут, как успешно применить их на практике. Регистрируйтесь сейчас, чтобы забронировать место и получить ценные знания. Посетите наш сайт для получения подробной информации. Ждем вас на вебинаре!',
      generationTool: GENERATION_TOOL.facebookPost,
    },
    [TOV_TEST_OPTION.liPostRenewable]: {
      title: 'Публикация в LinkedIn – Возобновляемая энергия',
      text: 'Прекрасные новости! Новые проекты компании ACME Corp по солнечной энергетике и соглашения по возобновляемой энергии меняют ситуацию в 11 штатах. Эти проекты добавят чистую энергию в местные сети, создадут рабочие места, поддержат сельские районы и продвинут достижение целей ACME Corp в области возобновляемой энергии. Наше портфолио включает более 600 проектов по возобновляемой энергетике в 10 странах. Узнайте, как эти инициативы позволят клиентам сэкономить средства и улучшат энергосеть США. Узнайте больше: [Вставьте ссылку]',
      generationTool: GENERATION_TOOL.linkedinPost,
    },
    [TOV_TEST_OPTION.liPostWorkplace]: {
      title: 'Публикация в LinkedIn – Лучшее место для работы',
      text: 'Мы рады сообщить, что компания ACME Corp вошла в список лучших мест для работы по версии Glassdoor в 2024 году! Эта честь отражает приверженность нашей команды и поддерживающую и инклюзивную рабочую среду, которую мы создали. Мы благодарны каждому члену нашей организации за их вклад. Спасибо Glassdoor и нашей невероятной команде. Желаем дальнейших успехов и роста!',
      generationTool: GENERATION_TOOL.linkedinPost,
    },
    [TOV_TEST_OPTION.emailFeedbackSurvey]: {
      title: 'Электронное письмо с опросом обратной связи',
      text: 'Уважаемый/ая [Имя клиента], в компании ACME Corp мы стремимся предоставлять исключительный сервис для клиентов, таких как вы. Мы будем очень признательны за ваш отзыв через короткий опрос, который займет всего 5 минут. Ваши честные ответы помогут нам улучшить наши продукты/услуги. Опрос анонимен, и ваше участие действительно имеет значение. Нажмите здесь, чтобы пройти опрос: [Ссылка на опрос] Спасибо за ваше время и поддержку! С уважением, Команда ACME Corp',
      generationTool: GENERATION_TOOL.emailBody,
    },
    [TOV_TEST_OPTION.emailFeatureAnnouncement]: {
      title: 'Электронное письмо с анонсом новой функции',
      text: 'Уважаемый/ая [Имя клиента], рады представить новую функцию, которая улучшит ваш опыт работы с ACME Corp! Наше последнее обновление включает расширенную аналитику данных для получения более глубоких инсайтов и принятия обоснованных решений. Пользуйтесь анализом в реальном времени и настройте отчеты в соответствии с вашими потребностями. Мы уверены, что это нововведение повысит вашу производительность и оптимизирует рабочие процессы. Войдите в систему, чтобы узнать больше о новой функции уже сегодня! Благодарим вас за постоянную поддержку. С уважением, Команда ACME Corp',
      generationTool: GENERATION_TOOL.emailBody,
    },
  },
  [LANGUAGE.sv]: {
    [TOV_TEST_OPTION.fbPostWebinar]: {
      title: 'Facebook-Post – Webinar-Einladung',
      text: 'Gå med i det exklusiva webbinariet från ACME Corp! Upptäck de senaste branschtrenderna, experttips och strategier för framgång. Lär dig av branschledare och nätverk med likasinnade. Våra experter kommer att dela med sig av sin kunskap om den senaste utvecklingen och hur man använder dem framgångsrikt. Registrera dig nu för att säkra din plats för säkra och värdefulla insikter. Besök vår hemsida för mer information. Vi ser fram emot att se dig!',
      generationTool: GENERATION_TOOL.facebookPost,
    },
    [TOV_TEST_OPTION.liPostRenewable]: {
      title: 'LinkedIn-Post – Erneuerbari Energie',
      text: 'Spännande nyheter! ACME Corps nya solenergiprojekt och kontrakt för förnybar energi omfattar 11 kantoner. Projekten för in ren energi till det lokala nätet, skapar jobb, stödjer landsbygdsområden och främjar ACME Corps mål för förnybar energi. Vår portfölj omfattar över 600 projekt för förnybar energi i 10 länder. Lär dig mer om initiativen och hur de minskar kostnaderna för Kina och förbättrar energinätet. Läs mer: [Lägg till länk]',
      generationTool: GENERATION_TOOL.linkedinPost,
    },
    [TOV_TEST_OPTION.liPostWorkplace]: {
      title: 'LinkedIn-Post – Bester Arbeitsplatz',
      text: 'Vi är glada över att ACME Corp har utsetts till en av Glassdoors bästa arbetsplatser för 2024! Priset uppmärksammar vårt teams engagemang och den inkluderande och stödjande arbetsmiljö vi skapar. Jag är tacksam mot alla medlemmar i vår organisation för deras bidrag. Stort tack till Glassdoor och vårt fantastiska team. Till fortsatt stor framgång och tillväxt!',
      generationTool: GENERATION_TOOL.linkedinPost,
    },
    [TOV_TEST_OPTION.emailFeedbackSurvey]: {
      title: 'E-Mail – Feedback Umfrag',
      text: 'Bästa [namn], På ACME Corp gör vi allt för att tillfredsställa våra kunder som du i alla avseenden. Vi skulle bli väldigt glada om du tog dig tid till en kort 5-minutersundersökning. Dina ärliga svar skulle hjälpa oss att förbättra våra produkter/tjänster. Undersökningen är anonym och ditt deltagande gör verkligen skillnad. Klicka här för att svara på undersökningen: [Enkätlänk] Tack så mycket för din tid och ditt stöd! Liebi Grüess, s’Team från ACME Corp',
      generationTool: GENERATION_TOOL.emailBody,
    },
    [TOV_TEST_OPTION.emailFeatureAnnouncement]: {
      title: 'E-Mail – Funktionä-Update',
      text: 'Bästa [Namn], vi är glada över att kunna presentera dig för en ny funktion som kommer att göra din ACME Corp-upplevelse ännu bättre! Vår senaste uppdatering inkluderar avancerad dataanalys för djupare insikter och välgrundade beslut. Njut av realtidsanalyser och personliga rapporter skräddarsydda efter dina behov. Jag tror att innovation ökar din produktivitet och optimerar ditt arbetsflöde. Logga in nu för att upptäcka den nya funktionen! Tack så mycket för ditt ständiga stöd. Liebi Grüess, s’Team från ACME Corp',
      generationTool: GENERATION_TOOL.emailBody,
    },
  },
  [LANGUAGE.cmn]: {
    [TOV_TEST_OPTION.fbPostWebinar]: {
      title: 'Facebook帖子 – 研讨会邀请',
      text: '加入ACME Corp的独家研讨会吧！了解最新的行业趋势、专家见解以及帮助提升业务的实用策略。向行业领导者学习，与志同道合的专业人士建立联系。我们的专家将分享最新发展和如何将它们应用于成功的知识。立即注册以确保您的席位，并获取宝贵的见解。访问我们的网站了解更多详情。期待您的参与！',
      generationTool: GENERATION_TOOL.facebookPost,
    },
    [TOV_TEST_OPTION.liPostRenewable]: {
      title: 'LinkedIn帖子 – 可再生能源',
      text: '振奋人心的消息！ACME Corp的新社区太阳能项目和可再生能源协议正在改变11个州的能源格局。这些项目将为当地电网带来清洁能源，创造就业机会，支持农村地区发展，并推进ACME Corp在可再生能源领域的目标。我们的项目组合涵盖10个国家的600多个可再生能源项目。了解这些计划将如何为客户节省成本并提升美国能源网的效能。了解更多信息：[插入链接]',
      generationTool: GENERATION_TOOL.linkedinPost,
    },
    [TOV_TEST_OPTION.liPostWorkplace]: {
      title: 'LinkedIn帖子 – 最佳工作场所',
      text: '我们非常高兴地宣布，ACME Corp被Glassdoor评为2024年最佳工作场所之一！这一荣誉体现了我们团队的奉献精神和我们所构建的包容性、支持性工作环境。我们感谢每一位组织成员的贡献。感谢Glassdoor和我们出色的团队。愿我们继续取得成功和成长！',
      generationTool: GENERATION_TOOL.linkedinPost,
    },
    [TOV_TEST_OPTION.emailFeedbackSurvey]: {
      title: '反馈调查邮件',
      text: '尊敬的[客户姓名]，在ACME Corp，我们致力于为像您这样的客户提供卓越的体验。我们非常希望您能够花费5分钟时间完成一份简短的问卷调查。您的诚实反馈将帮助我们改进产品/服务。该调查是匿名的，您的参与将真正产生影响。点击此处进行调查：[调查链接]。感谢您的时间和支持！此致，ACME Corp团队',
      generationTool: GENERATION_TOOL.emailBody,
    },
    [TOV_TEST_OPTION.emailFeatureAnnouncement]: {
      title: '功能公告邮件',
      text: '尊敬的[客户姓名]，我们很高兴向您介绍一项可以提升您在ACME Corp体验的新功能！我们的最新更新包含高级数据分析功能，可为您提供更深入的见解并帮助做出明智的决策。享受实时分析和定制化报告，这些功能均可根据您的需求进行调整。我们相信这项创新将提升您的工作效率并简化工作流程。立即登录探索这一新功能吧！感谢您一如既往的支持。此致，ACME Corp团队',
      generationTool: GENERATION_TOOL.emailBody,
    },
  },
  [LANGUAGE.yue]: {
    [TOV_TEST_OPTION.fbPostWebinar]: {
      title: 'Facebook 帖子 – 網上研討會邀請',
      text: '嚟參加 ACME Corp 嘅獨家網上研討會啦！了解最新嘅行業趨勢、專家嘅見解同埋可以幫助你提升業務嘅實用策略。向行業領導者學習，並同志同道合嘅專業人士聯絡。專家會分享最新嘅發展同埋點樣成功咁應用。即刻登記確保你嘅位置，攞取寶貴嘅見解。去我哋嘅網站睇詳情啦，期待喺度見到你！',
      generationTool: GENERATION_TOOL.facebookPost,
    },
    [TOV_TEST_OPTION.liPostRenewable]: {
      title: 'LinkedIn 帖子 – 再生能源',
      text: '好消息！ACME Corp 嘅社區太陽能項目同可再生能源協議喺度改變 11 個州嘅能源格局。呢啲項目會為當地電網帶嚟清潔能源，創造職位，支持鄉郊地區發展，推動ACME Corp 喺可再生能源嘅目標。公司嘅項目組合覆蓋咗 10 個國家，超過 600 個可再生能源項目。了解下呢啲計劃點樣為客戶節省成本，提升美國嘅能源網效能。了解更多：[插入連結]',
      generationTool: GENERATION_TOOL.linkedinPost,
    },
    [TOV_TEST_OPTION.liPostWorkplace]: {
      title: 'LinkedIn 帖子 – 最佳工作場所',
      text: '我哋好開心宣佈，ACME Corp 獲 Glassdoor 評選為 2024 年最佳工作場所之一！呢個榮譽反映咗我哋團隊嘅付出同埋我哋建立嘅包容性、支持性嘅工作環境。感激我哋機構每一位成員嘅貢獻。多謝 Glassdoor 同我哋嘅勁好嘅團隊。期待持續成功同埋增長！',
      generationTool: GENERATION_TOOL.linkedinPost,
    },
    [TOV_TEST_OPTION.emailFeedbackSurvey]: {
      title: '意見調查電郵',
      text: '親愛嘅 [客戶名]，喺ACME Corp，我哋致力為你呢類型嘅客戶提供卓越嘅體驗。我哋好希望你可以花5分鐘填寫一份短嘅問卷。你嘅真實回饋對我哋改善產品/服務好有幫助。呢份問卷係匿名嘅，而你嘅參與真係會有大嘅影響。點擊呢度填寫問卷：[問卷連結]。多謝你嘅時間同埋支持！ACME Corp 團隊敬上',
      generationTool: GENERATION_TOOL.emailBody,
    },
    [TOV_TEST_OPTION.emailFeatureAnnouncement]: {
      title: '功能公告電郵',
      text: '親愛嘅 [客戶名]，我哋好高興向你介紹一個可以提升你喺ACME Corp體驗嘅新功能！我哋最新嘅更新包含進階嘅數據分析功能，可以幫你獲得更深入嘅見解，做出更明智嘅決定。享受即時分析同埋可定製嘅報告，全部都可以根據你嘅需求調整。我哋相信呢項創新可以提升你嘅工作效率，簡化工作流程。即刻登入嚟探索呢個新功能！多謝你一直以嚟嘅支持。ACME Corp 團隊敬上',
      generationTool: GENERATION_TOOL.emailBody,
    },
  },
  [LANGUAGE.ja]: {
    [TOV_TEST_OPTION.fbPostWebinar]: {
      title: 'Facebook投稿 – ウェビナー招待',
      text: `ACME Corpの独占ウェビナーに参加しましょう！業界の最新トレンド、専門家の洞察、ビジネスを推進する実用的な戦略をご紹介します。業界のリーダーから学び、志を同じくするプロフェッショナルとつながりましょう。専門家が最新の開発について知識を共有し、成功にどう応用するかをお伝えします。貴重な情報を得るために今すぐ登録し、お席を確保してください。詳細については当社のウェブサイトをご覧ください。お会いできることを楽しみにしています！`,
      generationTool: GENERATION_TOOL.facebookPost,
    },
    [TOV_TEST_OPTION.liPostRenewable]: {
      title: 'LinkedIn投稿 – 再生可能エネルギー',
      text: `素晴らしいニュース！ACME Corpの新しいコミュニティソーラープロジェクトと再生可能エネルギー契約が11州を変えています。これらのプロジェクトは地元の電力網にクリーンエネルギーを供給し、雇用を創出し、農村地域を支援し、ACME Corpの再生可能エネルギー目標を推進します。当社のポートフォリオには、10カ国で600以上の再生可能エネルギープロジェクトが含まれています。これらの取り組みが顧客のコスト削減と米国のエネルギーネットワークの向上にどのように貢献するかをご覧ください。詳細はこちら：[リンクを挿入]`,
      generationTool: GENERATION_TOOL.linkedinPost,
    },
    [TOV_TEST_OPTION.liPostWorkplace]: {
      title: 'LinkedIn投稿 – ベストワークプレイス',
      text: `ACME CorpがGlassdoorによって2024年のベストワークプレイスの1つに選ばれたことをお知らせできることを嬉しく思います！この栄誉は、当社のチームの��身と、当社が構築した包括的で支持的な職場環境を反映しています。組織のすべてのメンバーに感謝します。Glassdoorと素晴らしいチームに感謝します。今後も成功と成長をお祈りしています！`,
      generationTool: GENERATION_TOOL.linkedinPost,
    },
    [TOV_TEST_OPTION.emailFeedbackSurvey]: {
      title: 'フィードバック調査メール',
      text: `尊敬する[顧客名]様、ACME Corpでは、お客様のようなお客様に優れた体験を提供することに努めています。お時間を割いて5分間の簡単なアンケ��トにお答えいただけると大変嬉しく思います。率直な回答は、製品/サービスの改善に役立ちます。アンケートは匿名です。ご参加いただくと、本当に違いを生むことができます。アンケートにご参加いただくにはこちらをクリックしてください：[アンケートリンク]。お時間とご支援に感謝いたします！敬具、ACME Corpチーム`,
      generationTool: GENERATION_TOOL.emailBody,
    },
    [TOV_TEST_OPTION.emailFeatureAnnouncement]: {
      title: '機能アナウンスメール',
      text: `尊敬する[顧客名]様、ACME Corpでお客様の体験をさらに向上させる新機能をご紹介できることを大変嬉しく思います！最新の更新には、より深い洞察を得るための高度なデータ分析機能が含まれており、より賢明な意思決定を支援します。リアルタイム分析とニーズに合わせて調整可能なレポートをお楽しみください。この革新が生産性を向上させ、ワークフローを最適化すると信じています。新機能を探索するには、今すぐログインしてください！ご継続的なサポートに感謝いたします。敬具、ACME Corpチーム`,
      generationTool: GENERATION_TOOL.emailBody,
    },
  },
  [LANGUAGE.zh_tw]: {
    [TOV_TEST_OPTION.fbPostWebinar]: {
      title: 'Facebook帖子 – 網路研討會邀請',
      text: '加入ACME Corp的獨家網路研討會吧！了解最新的行業趨勢、專家見解以及幫助提升業務的實用策略。向行業領導者學習，並與志同道合的專業人士聯絡。專家將分享最新的發展以及如何成功地應用。立即註冊以確保您的席位，並獲取寶貴的見解。造訪我們的網站了解更多詳情。期待您的參與！',
      generationTool: GENERATION_TOOL.facebookPost,
    },
    [TOV_TEST_OPTION.liPostRenewable]: {
      title: 'LinkedIn帖子 – 再生能源',
      text: '好消息！ACME Corp的新社區太陽能項目和再生能源協議正在改變11個州的能源格局。這些項目將為當地電網帶來清潔能源，創造就業機會，支持鄉郊地區發展，推動ACME Corp在再生能源的目標。公司的項目組合涵蓋10個國家，超過600個再生能源項目。了解這些計劃將如何為客戶節省成本，提升美國的能源網效能。了解更多信息：[插入連結]',
      generationTool: GENERATION_TOOL.linkedinPost,
    },
    [TOV_TEST_OPTION.liPostWorkplace]: {
      title: 'LinkedIn帖子 – 最佳工作場所',
      text: '我們非常高興地宣布，ACME Corp被Glassdoor評選為2024年最佳工作場所之一！這一榮譽反映了我們團隊的奉獻精神和我們所構建的包容性、支持性工作環境。我們感謝每一位組織成員的貢獻。感謝Glassdoor和我們出色的團隊。期待持續成功和成長！',
      generationTool: GENERATION_TOOL.linkedinPost,
    },
    [TOV_TEST_OPTION.emailFeedbackSurvey]: {
      title: '反饋調查郵件',
      text: '尊敬的[客戶名]，在ACME Corp，我們致力於為像您這樣的客戶提供卓越的體驗。我們非常希望您能夠花費5分鐘時間完成一份簡短的問卷。您的誠實反饋將幫助我們改進產品/服務。該調查是匿名的，您的參與將真正產生影響。點擊此處進行調查：[調查連結]。感謝您的時間和支持！此致，ACME Corp團隊',
      generationTool: GENERATION_TOOL.emailBody,
    },
    [TOV_TEST_OPTION.emailFeatureAnnouncement]: {
      title: '功能公告郵件',
      text: '尊敬的[客戶名]，我們很高興向您介紹一項可以提升您在ACME Corp體驗的新功能！我們的最新更新包含高級數據分析功能，可為您提供更深入的見解並幫助做出明智的決策。享受實時分析和可定製報告，這些功能均可根據您的需求進行調整。我們相信這項創新將提升您的工作效率並簡化工作流程。立即登錄探索這一新功能吧！感謝您一如既往的支持。此致，ACME Corp團隊',
      generationTool: GENERATION_TOOL.emailBody,
    },
  },
  [LANGUAGE.zh_cn]: {
    [TOV_TEST_OPTION.fbPostWebinar]: {
      title: 'Facebook帖子 – 网络研讨会邀请',
      text: '加入ACME Corp的独家网络研讨会吧！了解最新的行业趋势、专家见解以及帮助提升业务的实用策略。向行业领导者学习，并与志同道合的专业人士联系。专家将分享最新的发展以及如何成功地应用。立即注册以确保您的席位，并获取宝贵的见解。访问我们的网站了解更多详情。期待您的参与！',
      generationTool: GENERATION_TOOL.facebookPost,
    },
    [TOV_TEST_OPTION.liPostRenewable]: {
      title: 'LinkedIn帖子 – 可再生能源',
      text: '好消息！ACME Corp的新社区太阳能项目和可再生能源协议正在改变11个州的能源格局。这些项目将为当地电网带来清洁能源，创造就业机会，支持农村地区发展，并推动ACME Corp在可再生能源的目标。公司的项目组合涵盖10个国家，超过600个可再生能源项目。了解这些计划将如何为客户节省成本，提升美国的能源网效能。了解更多信息：[插入链接]',
      generationTool: GENERATION_TOOL.linkedinPost,
    },
    [TOV_TEST_OPTION.liPostWorkplace]: {
      title: 'LinkedIn帖子 – 最佳工作场所',
      text: '我们非常高兴地宣布，ACME Corp被Glassdoor评为2024年最佳工作场所之一！这一荣誉体现了我们团队的奉献精神和我们所构建的包容性、支持性工作环境。我们感谢每一位组织成员的贡献。感谢Glassdoor和我们出色的团队。愿我们继续取得成功和成长！',
      generationTool: GENERATION_TOOL.linkedinPost,
    },
    [TOV_TEST_OPTION.emailFeedbackSurvey]: {
      title: '反馈调查邮件',
      text: '尊敬的[客户名]，在ACME Corp，我们致力为像您这样的客户提供卓越的体验。我们非常希望您能够花费5分钟时间完成一份简短的问卷。您的诚实反馈将帮助我们改进产品/服务。该调查是匿名的，您的参与将真正产生影响。点击此处进行调查：[调查链接]。感谢您的时间和支持！此致，ACME Corp团队',
      generationTool: GENERATION_TOOL.emailBody,
    },
    [TOV_TEST_OPTION.emailFeatureAnnouncement]: {
      title: '功能公告邮件',
      text: '尊敬的[客户名]，我们很高兴向您介绍一项可以提升您在ACME Corp体验的新功能！我们的最新更新包含高级数据分析功能，可为您提供更深入的见解并帮助做出明智的决策。享受实时分析和定制化报告，这些功能均可根据您的需求进行调整。我们相信这项创新将提升您的工作效率并简化工作流程。立即登录探索这一新功能吧！感谢您一如既往的支持。此致，ACME Corp团队',
      generationTool: GENERATION_TOOL.emailBody,
    },
  },
  [LANGUAGE.ms]: {
    [TOV_TEST_OPTION.fbPostWebinar]: {
      title: 'Hantaran Facebook – Jemputan Webinar',
      text: 'Sertai webinar eksklusif ACME Corp! Ketahui trend terkini dalam industri, pandangan pakar dan strategi praktikal untuk meningkatkan perniagaan anda. Belajar dari pemimpin industri dan berhubung dengan profesional yang berkongsi minat. Pakar kami akan berkongsi pengetahuan tentang perkembangan terkini dan bagaimana mengaplikasikannya dengan berjaya. Daftar sekarang untuk memastikan tempat anda dan dapatkan pandangan berharga. Lawati laman web kami untuk maklumat lanjut. Kami berharap dapat berjumpa dengan anda!',
      generationTool: GENERATION_TOOL.facebookPost,
    },
    [TOV_TEST_OPTION.liPostRenewable]: {
      title: 'Hantaran LinkedIn – Tenaga Boleh Diperbaharui',
      text: 'Berita menggembirakan! Projek komuniti solar baru dan perjanjian tenaga boleh diperbaharui ACME Corp sedang mengubah landskap tenaga 11 negeri di seluruh Amerika Syarikat. Projek-projek ini akan membawa tenaga bersih ke rangkaian elektrik tempatan, mencipta peluang pekerjaan, menyokong pembangunan kawasan luar bandar dan memajukan matlamat tenaga boleh diperbaharui ACME Corp. Portofolio kami merangkumi lebih daripada 600 projek tenaga boleh diperbaharui di lebih daripada 10 buah negara. Ketahui bagaimana rancangan ini akan menjimatkan kos pelanggan dan meningkatkan kecekapan rangkaian tenaga Amerika Syarikat. Ketahui lebih lanjut: [Masukkan Pautan]',
      generationTool: GENERATION_TOOL.linkedinPost,
    },
    [TOV_TEST_OPTION.liPostWorkplace]: {
      title: 'Hantaran LinkedIn – Tempat Kerja Terbaik',
      text: 'Kami sangat gembira untuk mengumumkan bahawa ACME Corp telah dipilih sebagai salah satu tempat kerja terbaik Glassdoor untuk tahun 2024! Penghormatan ini mencerminkan komitmen pasukan kami dan persekitaran kerja inklusif dan sokongan yang kami bina. Kami berterima kasih kepada setiap ahli organisasi kami atas sumbangan mereka. Terima kasih kepada Glassdoor dan pasukan hebat kami. Semoga kami terus berjaya dan berkembang!',
      generationTool: GENERATION_TOOL.linkedinPost,
    },
    [TOV_TEST_OPTION.emailFeedbackSurvey]: {
      title: 'E-mel Soal Selidik Maklum Balas',
      text: 'Kepada [Nama Pelanggan], di ACME Corp, kami berusaha untuk memberikan pengalaman yang cemerlang kepada pelanggan seperti anda. Kami sangat berharap anda dapat meluangkan masa 5 minit untuk menyiapkan soal selidik ringkas. Maklum balas jujur anda akan membantu kami meningkatkan produk / perkhidmatan. Soal selidik ini adalah anonim, dan penyertaan anda akan memberi kesan yang sebenar. Klik di sini untuk menjalankan soal selidik: [Pautan Soal Selidik]. Terima kasih atas masa dan sokongan anda! Hormat kami, Pasukan ACME Corp',
      generationTool: GENERATION_TOOL.emailBody,
    },
    [TOV_TEST_OPTION.emailFeatureAnnouncement]: {
      title: 'E-mel Pengumuman Ciri',
      text: 'Kepada [Nama Pelanggan], kami sangat gembira untuk memperkenalkan ciri baru yang dapat meningkatkan pengalaman anda di ACME Corp! Kemas kini terbaru kami mengandungi ciri analisis data lanjutan yang dapat memberikan anda pandangan yang lebih mendalam dan membantu membuat keputusan yang bijak. Nikmati analisis secara langsung dan laporan yang boleh disesuaikan, yang semuanya boleh disesuaikan mengikut keperluan anda. Kami yakin inovasi ini akan meningkatkan kecekapan kerja anda dan menyederhanakan aliran kerja. Log masuk sekarang untuk meneroka ciri baru ini! Terima kasih atas sokongan anda yang berterusan. Hormat kami, Pasukan ACME Corp',
      generationTool: GENERATION_TOOL.emailBody,
    },
  },
  [LANGUAGE.ko]: {
    [TOV_TEST_OPTION.fbPostWebinar]: {
      title: 'Facebook 게시물 – 웨비나 초대',
      text: 'ACME Corp의 독점 웨비나에 참여하세요! 최신 업계 트렌드, 전문가 인사이트, 비즈니스를 향상시킬 수 있는 실행 가능한 전략을 발견하세요. 업계 리더들로부터 배우고 같은 관심사를 가진 전문가들과 네트워킹하세요. 우리 전문가들이 최신 발전과 성공적인 적용 방법에 대한 지식을 공유할 것입니다. 지금 등록하여 자리를 확보하고 가치 있는 인사이트를 얻으세요. 자세한 내용은 우리 웹사이트를 방문하세요. 여러분을 만나뵙기를 기대합니다!',
      generationTool: GENERATION_TOOL.facebookPost,
    },
    [TOV_TEST_OPTION.liPostRenewable]: {
      title: 'LinkedIn 게시물 – 재생에너지',
      text: '흥미로운 소식입니다! ACME Corp의 새로운 커뮤니티 태양광 프로젝트와 재생에너지 계약이 미국 전역의 11개 주를 변화시키고 있습니다. 이 프로젝트들은 지역 전력망에 깨끗한 에너지를 추가하고, 일자리를 창출하며, 농촌 지역을 지원하고, ACME Corp의 재생에너지 목표를 발전시킬 것입니다. 우리 포트폴리오는 10개국 이상의 600개 이상의 재생에너지 프로젝트를 포함합니다. 이 이니셔티브들이 고객 비용을 절약하고 미국 에너지 그리드를 향상시킬 방법을 알아보세요. 자세히 보기: [링크 삽입]',
      generationTool: GENERATION_TOOL.linkedinPost,
    },
    [TOV_TEST_OPTION.liPostWorkplace]: {
      title: 'LinkedIn 게시물 – 최고의 직장',
      text: 'ACME Corp이 2024년 Glassdoor 최고의 직장 중 하나로 선정되었다는 소식을 발표하게 되어 매우 기쁩니다! 이 영예는 우리 팀의 헌신과 우리가 구축한 포용적이고 지원적인 근무 환경을 반영합니다. 우리는 조직의 모든 구성원들의 기여에 감사드립니다. Glassdoor와 우리의 놀라운 팀에게 감사드립니다. 지속적인 성공과 성장을 기대합니다!',
      generationTool: GENERATION_TOOL.linkedinPost,
    },
    [TOV_TEST_OPTION.emailFeedbackSurvey]: {
      title: '피드백 설문 이메일',
      text: '안녕하세요 [고객명]님, ACME Corp에서는 귀하와 같은 고객들에게 탁월한 경험을 제공하기 위해 노력하고 있습니다. 짧은 5분 설문을 통해 귀하의 피드백을 받을 수 있어서 매우 기쁩니다. 귀하의 정직한 답변은 우리 제품/서비스를 개선하는 데 도움이 될 것입니다. 이 설문은 익명이며, 귀하의 참여는 진정한 변화를 만들 것입니다. 설문을 진행하려면 여기를 클릭하세요: [설문 링크]. 귀하의 시간과 지원에 감사드립니다! 진심을 담아, ACME Corp 팀',
      generationTool: GENERATION_TOOL.emailBody,
    },
    [TOV_TEST_OPTION.emailFeatureAnnouncement]: {
      title: '기능 발표 이메일',
      text: '안녕하세요 [고객명]님, ACME Corp 경험을 향상시킬 새로운 기능을 소개하게 되어 매우 기쁩니다! 우리의 최신 업데이트는 더 깊은 인사이트와 정보에 기반한 결정을 위한 고급 데이터 분석을 포함합니다. 실시간 분석과 귀하의 필요에 맞게 조정된 맞춤형 보고서를 즐기세요. 우리는 이 혁신이 귀하의 생산성을 향상시키고 워크플로우를 간소화할 것이라고 믿습니다. 지금 로그인하여 이 새로운 기능을 탐험해보세요! 지속적인 지원에 감사드립니다. 진심을 담아, ACME Corp 팀',
      generationTool: GENERATION_TOOL.emailBody,
    },
  },
  [LANGUAGE.th]: {
    [TOV_TEST_OPTION.fbPostWebinar]: {
      title: 'โพสต์ Facebook – เชิญเข้าร่วมเว็บบินาร์',
      text: 'เข้าร่วมเว็บบินาร์พิเศษของ ACME Corp! ค้นพบแนวโน้มล่าสุดในอุตสาหกรรม ความคิดเชี่ยวชาญ และกลยุทธ์ที่เป็นประโยชน์สำหรับการพัฒนาธุรกิจของคุณ เรียนรู้จากผู้นำในอุตสาหกรรมและเชื่อมต่อกับผู้เชี่ยวชาญที่มีความสนใจเดียวกัน ผู้เชี่ยวชาญของเราจะแบ่งปันความรู้เกี่ยวกับความก้าวหน้าล่าสุดและวิธีการนำมันไปใช้ให้สำเร็จ ลงทะเบียนเดี๋ยวนี้เพื่อรักษาที่นั่งของคุณและรับข้อมูลที่มีค่า ไปที่เว็บไซต์ของเราเพื่อข้อมูลเพิ่มเติม หวังว่าจะได้พบคุณ!',
      generationTool: GENERATION_TOOL.facebookPost,
    },
    [TOV_TEST_OPTION.liPostRenewable]: {
      title: 'โพสต์ LinkedIn – พลังงานสามัญ',
      text: 'ข่าวดี! โครงการพลังงานแสงอาทิตย์ชุมชนและข้อตกลงพลังงานสามัญของ ACME Corp กำลังเปลี่ยนแปลงภูมิทัศน์พลังงานใน 11 รัฐทั่วสหรัฐอเมริกา โครงการเหล่านี้จะนำพลังงานสะอาดเข้าสู่เครือข่ายไฟฟ้าท้องถิ่น สร้างงาน สนับสนุนพื้นที่ชนบท และส่งเสริมเป้าหมายพลังงานสามัญของ ACME Corp พอร์ตโฟลิโอของเรารวมไปถึงโครงการพลังงานสามัญมากกว่า 600 โครงการ รู้ว่าโครงการเหล่านี้จะช่วยลดค่าใช้จ่ายของลูกค้าและเพิ่มประสิทธิภาพของเครือข่ายพลังงานของสหรัฐอเมริกา ดูข้อมูลเพิ่มเติม: [แทรกลิงค์]',
      generationTool: GENERATION_TOOL.linkedinPost,
    },
    [TOV_TEST_OPTION.liPostWorkplace]: {
      title: 'โพสต์ LinkedIn – สถานที่ทำงานยอดเยี่ยม',
      text: 'เรามีความยินดีอย่างยิ่งที่จะประกาศว่า ACME Corp ได้รับการเลือกเป็นหนึ่งในสถานที่ทำงานยอดเยี่ยมของ Glassdoor ปี 2024! ความเป็นเกียรตินี้สะท้อนความมุ่งมั่นของทีมของเราและสภาพแวดล้อมการทำงานที่รองรับและสร้างขึ้น ขอขอบคุณสมาชิกทุกคนในองค์กรของเราสำหรับการมอบหมายงานของพวกเขา ขอขอบคุณ Glassdoor และทีมของเราที่ยอดเยี่ยม หวังว่าเราจะดำเนินการไปอีกไกลและเติบโต!',
      generationTool: GENERATION_TOOL.linkedinPost,
    },
    [TOV_TEST_OPTION.emailFeedbackSurvey]: {
      title: 'อีเมลแบบสำรวจความคิดเห็น',
      text: 'เรียน [ชื่อลูกค้า] ที่ ACME Corp, เรามุ่งเน้นการให้บริการที่ยอดเยี่ยมสำหรับลูกค้าเช่นคุณ เราหวังว่าคุณจะใช้เวลา 5 นาทีในการกรอกแบบสำรวจสั้นๆ ความคิดเห็นของคุณจะช่วยเราปรับปรุงผลิตภัณฑ์ / บริการ แบบสำรวจนี้เป็นแบบไม่ระบุชื่อ และการเข้าร่วมของคุณจะมีผลจริง คลิกที่นี่เพื่อทำแบบสำรวจ: [ลิงก์แบบสำรวจ] ขอบคุณสำหรับเวลาและการสนับสนุนของคุณ! ขอแสดงความนับถือ, ทีมงาน ACME Corp',
      generationTool: GENERATION_TOOL.emailBody,
    },
    [TOV_TEST_OPTION.emailFeatureAnnouncement]: {
      title: 'อีเมลประกาศคุณสมบัติ',
      text: 'เรียน [ชื่อลูกค้า] เรามีความยินดีอย่างยิ่งที่จะแนะนำคุณสมบัติใหม่ที่จะเพิ่มประสิทธิภาพในการใช้งานของคุณใน ACME Corp! การอัพเดตล่าสุดของเรารวมถึงคุณสมบัติการวิเคราะห์ข้อมูลขั้นสูงที่สามารถให้คุณมองเห็นภาพรวมลึกซึ้งและช่วยในการตัดสินใจอย่างมีเหตุผล สนุกกับการวิเคราะห์แบบเรียลไทม์และรายงานที่สามารถปรับแต่งตามความต้องการของคุณ ที่เราเชื่อว่านวัตกรรมนี้จะเพิ่มประสิทธิภาพในการทำงานของคุณและทำให้กระบวนการทำงานเรียบง่ายขึ้น ล็อกอินเข้าสู่การสำรวจคุณสมบัติใหม่นี้! ขอบคุณสำหรับการสนับสนุนของคุณตลอดเวลา ขอแสดงความนับถือ, ทีมงาน ACME Corp',
      generationTool: GENERATION_TOOL.emailBody,
    },
  },
  [HIDDEN_LANGUAGE.he]: {
    [TOV_TEST_OPTION.fbPostWebinar]: {
      title: 'פוסט בפייסבוק – הזמנה לוובינר',
      text: 'הצטרפו לוובינר הבלעדי של ACME Corp! גלו את הטרנדים העדכניים בתעשייה, תובנות מקצועיות ואסטרטגיות מעשיות לשיפור העסק שלכם. למדו ממובילי התחום וצרו קשרים עם אנשי מקצוע בעלי תחומי עניין דומים. המומחים שלנו ישתפו בידע על החידושים האחרונים ואיך ליישם אותם בהצלחה. הירשמו עכשיו כדי להבטיח את מקומכם ולקבל תובנות חשובות. בקרו באתר שלנו לפרטים נוספים. נשמח לראות אתכם!',
      generationTool: GENERATION_TOOL.facebookPost,
    },
    [TOV_TEST_OPTION.liPostRenewable]: {
      title: 'פוסט ב-LinkedIn – אנרגיה מתחדשת',
      text: 'חדשות מרגשות! הפרויקטים הסולאריים הקהילתיים וההסכמים החדשים של ACME Corp לאנרגיה מתחדשת משנים את פני 11 מדינות ברחבי ארה"ב. פרויקטים אלו יוסיפו אנרגיה נקייה לרשתות המקומיות, ייצרו מקומות עבודה, יתמכו באזורים כפריים ויקדמו את יעדי האנרגיה המתחדשת של ACME Corp. הפורטפוליו שלנו כולל מעל 600 פרויקטים של אנרגיה מתחדשת. למדו כיצד יוזמות אלו יובילו לחיסכון עבור הלקוחות וישפרו את רשת האנרגיה של ארה"ב. למידע נוסף: [הכנס קישור]',
      generationTool: GENERATION_TOOL.linkedinPost,
    },
    [TOV_TEST_OPTION.liPostWorkplace]: {
      title: 'פוסט בלינקדאין - מקום עבודה מצטיין',
      text: 'אנו נרגשים להודיע כי ACME Corp נבחרה לאחד ממקומות העבודה הטובים ביותר של Glassdoor לשנת 2024! הכבוד הזה משקף את המחויבות של הצוות שלנו ואת סביבת העבודה התומכת והכוללת שבנינו. אנו מודים לכל חברי הצוות בארגון שלנו על תרומתם. תודה ל-Glassdoor ולצוות המדהים שלנו. קדימה להצלחה וצמיחה מתמשכת!',
      generationTool: GENERATION_TOOL.linkedinPost,
    },
    [TOV_TEST_OPTION.emailFeedbackSurvey]: {
      title: 'דוא"ל - בקשת משוב',
      text: 'לקוח/ה יקר/ה, ב-ACME Corp אנו שואפים להעניק חוויות יוצאות דופן ללקוחות כמוך. נשמח מאוד לקבל את המשוב שלך דרך סקר קצר של 5 דקות. התשובות הכנות שלך יעזרו לנו לשפר את המוצרים/שירותים שלנו. הסקר הוא אנונימי, והשתתפותך תעשה שינוי אמיתי. לחץ/י כאן כדי למלא את הסקר: [קישור לסקר] תודה רבה על הזמן והתמיכה שלך! בברכה, צוות ACME Corp',
      generationTool: GENERATION_TOOL.emailBody,
    },
    [TOV_TEST_OPTION.emailFeatureAnnouncement]: {
      title: 'דוא"ל - הכרזה על פיצ\'ר חדש',
      text: "לקוח/ה יקר/ה, אנו שמחים להציג פיצ'ר חדש שישדרג את החוויה שלך עם ACME Corp! העדכון האחרון שלנו כולל ניתוח נתונים מתקדם לתובנות מעמיקות והחלטות מושכלות יותר. תהנה/י מניתוח בזמן אמת ודוחות מותאמים אישית לפי הצרכים שלך. אנו מאמינים שהחדשנות הזו תגביר את הפרודוקטיביות שלך ותייעל את תהליכי העבודה שלך. התחבר/י עכשיו כדי לחוות את הפיצ'ר החדש! תודה רבה על התמיכה המתמשכת שלך. בברכה, צוות ACME Corp",
      generationTool: GENERATION_TOOL.emailBody,
    },
  },
};

export const getTovTestOptionProps = (
  id: TovTestOption,
  language: Language
): TovTestOptionProps => {
  if (TOV_TEST_NON_ENGLISH_OPTION_PROPS[language]?.[id]) {
    return TOV_TEST_NON_ENGLISH_OPTION_PROPS[language][id];
  } else {
    return TOV_TEST_OPTION_PROPS[id];
  }
};

export const TONE_OF_VOICE_CREATION_MODE = {
  manual: 'manual',
  learnFromText: 'learnFromText',
  importFromResource: 'importFromResource',
  importFromURL: 'importFromURL',
  importFromDescription: 'importFromDescription',
} as const;
export type ToneOfVoiceCreationMode =
  (typeof TONE_OF_VOICE_CREATION_MODE)[keyof typeof TONE_OF_VOICE_CREATION_MODE];

export const TOV_DESCRIPTION_MAX_CHARS = 5000;
